/**
 * PHP API Adapter
 * Адаптер для работы с PHP Backend API
 */

import type { Ticket, Building, User, Notification, Center, PurchaseRequest, PurchaseRequestStatusHistory, Note, NoteColorTag, FkspRequest, DovolenaRequest, DovolenaRequestDay, DovolenaDashboard } from '../types'

// Используем относительный путь - автоматически определяется домен
const API_BASE_URL = window.location.origin + '/api'

// Auth via HttpOnly cookie — Authorization header optional
function getAuthToken(): string | null {
  return null
}

// Базовая функция для HTTP запросов
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  requireAuth: boolean = true
): Promise<T> {
  const token = getAuthToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  }

  // Добавляем токен только если он есть и требуется авторизация
  if (token && requireAuth) {
    headers['Authorization'] = `Bearer ${token}`
  } else if (token) {
    // Если токен есть, но авторизация не обязательна, все равно отправляем его
    headers['Authorization'] = `Bearer ${token}`
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    })

    // Читаем ответ один раз
    const contentType = response.headers.get('content-type')
    const isJson = contentType && contentType.includes('application/json')

    let responseData: any = null
    try {
      if (isJson) {
        responseData = await response.json()
      } else {
        const text = await response.text()
        responseData = text ? { error: text } : null
      }
    } catch (e) {
      console.error('Error parsing response:', e)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    }

    if (!response.ok) {
      // Если авторизация не требуется и получили 401, это нормально - просто возвращаем null
      if (!requireAuth && response.status === 401) {
        throw new Error('UNAUTHORIZED_PUBLIC_REQUEST')
      }

      // Обработка ошибок БД
      if (response.status === 500 && responseData?.error?.includes('Database')) {
        console.error('Database error:', responseData.error)
        throw new Error('DATABASE_ERROR')
      }

      const errorMessage = responseData?.error || responseData?.message || response.statusText || `HTTP ${response.status}`
      throw new Error(errorMessage)
    }

    return responseData
  } catch (error: any) {
    // Обработка сетевых ошибок и ошибок БД
    if (error.message === 'DATABASE_ERROR') {
      console.error('Database connection error')
      throw new Error('Ошибка подключения к базе данных. Пожалуйста, обновите страницу.')
    }
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network error:', error)
      throw new Error('Ошибка сети. Проверьте подключение к интернету.')
    }
    throw error
  }
}

// ============================================
// ŽÁDOSTI
// ============================================

export async function loadTickets(_userId: string, _userRole: string | null, _adminType?: string, limit?: number, offset?: number): Promise<Ticket[]> {
  // Лимиты для разных ролей
  let defaultLimit = 500 // По умолчанию для заявителей
  if (_userRole === 'admin' || _userRole === 'director') {
    defaultLimit = 10000 // Админы и директор видят все заявки
  } else if (_userRole === 'executor' || _userRole === 'vedouci_executor') {
    defaultLimit = 10000 // Řešitel: vlastní přiřazení (API max 10000)
  }
  const effectiveLimit = limit || defaultLimit
  const effectiveOffset = offset || 0

  const roleParam = _userRole ? `&role=${_userRole}` : ''
  const tickets = await apiRequest<any[]>(`/tickets?limit=${effectiveLimit}&offset=${effectiveOffset}${roleParam}`)

  // Преобразуем в формат Ticket
  return tickets.map(ticket => ({
    id: ticket.id,
    created_at: ticket.created_at,
    requester_id: ticket.requester_id,
    requester_name: ticket.requester_name,
    requester_email: ticket.requester_email,
    admin_id: ticket.admin_id,
    admin_name: ticket.admin_name,
    admin_type: ticket.admin_type,
    executor_id: ticket.executor_id,
    executor_name: ticket.executor_name,
    building_id: ticket.building_id,
    building_name: ticket.building_name,
    description: ticket.description,
    status: ticket.status,
    phone: ticket.phone,
    file_url: ticket.file_url,
    file_name: ticket.file_name,
    assigned_at: ticket.assigned_at,
    completed_at: ticket.completed_at,
    // Поля для тикета перемещения имущества
    ticket_type: ticket.ticket_type,
    from_building_id: ticket.from_building_id,
    from_building_name: ticket.from_building_name,
    from_location: ticket.from_location,
    inventory_number: ticket.inventory_number,
    to_building_id: ticket.to_building_id,
    to_building_name: ticket.to_building_name,
    to_location: ticket.to_location,
    is_warehouse: ticket.is_warehouse,
    assigned_to_id: ticket.assigned_to_id,
    assigned_to_name: ticket.assigned_to_name,
    assigned_to_email: ticket.assigned_to_email,
  })) as Ticket[]
}

/** Můj kabinet: závady kde je uživatel žadatel, řešitel, správce nebo příjemce. */
export async function loadTicketsForCabinet(limit = 500): Promise<Ticket[]> {
  const tickets = await apiRequest<any[]>(`/tickets?cabinet=1&limit=${limit}&offset=0`)
  return tickets.map((ticket) => ({
    id: ticket.id,
    created_at: ticket.created_at,
    requester_id: ticket.requester_id,
    requester_name: ticket.requester_name,
    requester_email: ticket.requester_email,
    admin_id: ticket.admin_id,
    admin_name: ticket.admin_name,
    admin_type: ticket.admin_type,
    executor_id: ticket.executor_id,
    executor_name: ticket.executor_name,
    building_id: ticket.building_id,
    building_name: ticket.building_name,
    description: ticket.description,
    status: ticket.status,
    phone: ticket.phone,
    file_url: ticket.file_url,
    file_name: ticket.file_name,
    assigned_at: ticket.assigned_at,
    completed_at: ticket.completed_at,
    ticket_type: ticket.ticket_type,
    from_building_id: ticket.from_building_id,
    from_building_name: ticket.from_building_name,
    from_location: ticket.from_location,
    inventory_number: ticket.inventory_number,
    to_building_id: ticket.to_building_id,
    to_building_name: ticket.to_building_name,
    to_location: ticket.to_location,
    is_warehouse: ticket.is_warehouse,
    assigned_to_id: ticket.assigned_to_id,
    assigned_to_name: ticket.assigned_to_name,
    assigned_to_email: ticket.assigned_to_email,
  })) as Ticket[]
}

export async function createTicket(
  _requesterId: string,
  adminId: string,
  buildingId: string,
  description: string,
  fileName?: string,
  fileUrl?: string,
  phone?: string
) {
  return apiRequest('/tickets', {
    method: 'POST',
    body: JSON.stringify({
      admin_id: adminId,
      building_id: buildingId,
      description,
      file_name: fileName,
      file_url: fileUrl,
      phone: phone || null,
    }),
  })
}

export async function createPropertyMoveTicket(data: {
  from_building_id: string
  from_location: string
  item_name: string
  inventory_number: string
  to_building_id: string | null
  to_location: string
  is_warehouse: boolean
  assigned_to_id: string | null
  requester_transport_option?: 'self' | 'organize'
}) {
  return apiRequest('/property-move-tickets', {
    method: 'POST',
    body: JSON.stringify({
      from_building_id: data.from_building_id,
      from_location: data.from_location,
      item_name: data.item_name,
      inventory_number: data.inventory_number,
      to_building_id: data.to_building_id,
      to_location: data.to_location,
      is_warehouse: data.is_warehouse,
      assigned_to_id: data.assigned_to_id,
      requester_transport_option: data.requester_transport_option,
    }),
  })
}

export async function loadPropertyMoveTickets(): Promise<Ticket[]> {
  return apiRequest<Ticket[]>('/property-move-tickets')
}

export async function loadPropertyMoveTicketsForCabinet(): Promise<Ticket[]> {
  return apiRequest<Ticket[]>('/property-move-tickets?cabinet=1')
}

export async function updatePropertyMoveTicketStatus(
  ticketId: string,
  status: 'new' | 'in_progress' | 'completed' | 'organize_transport'
) {
  return apiRequest(`/property-move-tickets/${ticketId}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  })
}

export async function confirmPropertyMoveTicketReceipt(ticketId: string) {
  return apiRequest(`/property-move-tickets/${ticketId}/confirm`, {
    method: 'PATCH',
  })
}

export async function respondToPropertyMoveTicket(
  ticketId: string,
  response: 'accepted_self_transport' | 'needs_transport' | 'rejected',
  rejectionReason?: string
) {
  return apiRequest(`/property-move-tickets/${ticketId}/recipient-response`, {
    method: 'PATCH',
    body: JSON.stringify({ response, rejection_reason: rejectionReason }),
  })
}

export async function rejectPropertyMoveTicketByReferent(
  ticketId: string,
  rejectionReason: string
) {
  return apiRequest(`/property-move-tickets/${ticketId}/reject-by-referent`, {
    method: 'PATCH',
    body: JSON.stringify({ rejection_reason: rejectionReason }),
  })
}

export async function deletePropertyMoveTicket(ticketId: string) {
  return apiRequest(`/property-move-tickets/${ticketId}`, {
    method: 'DELETE',
  })
}

export async function accelerateTicket(ticketId: string): Promise<{ success: boolean; message: string; emails_sent: number }> {
  return apiRequest(`/tickets/${ticketId}/accelerate`, {
    method: 'POST',
  })
}

export async function updateTicketStatus(
  ticketId: string,
  status: 'in_progress' | 'completed',
  executorId?: string,
  completionComment?: string,
  assignmentComment?: string,
) {
  const body: Record<string, unknown> = { status }
  if (executorId) {
    body.executor_id = executorId
  }
  if (status === 'completed' && completionComment !== undefined) {
    body.completion_comment = completionComment
  }
  const trimmedAssignment = assignmentComment?.trim()
  if (trimmedAssignment) {
    body.add_comment = trimmedAssignment
  }

  return apiRequest(`/tickets/${ticketId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function addTicketComment(ticketId: string, comment: string) {
  return apiRequest(`/tickets/${ticketId}`, {
    method: 'PUT',
    body: JSON.stringify({ add_comment: comment }),
  })
}

export async function deleteTicket(ticketId: string) {
  return apiRequest(`/tickets/${ticketId}`, {
    method: 'DELETE',
  })
}

// ============================================
// ЗДАНИЯ
// ============================================

export async function loadBuildings(): Promise<Building[]> {
  return apiRequest<Building[]>('/buildings')
}

export async function addBuilding(name: string) {
  return apiRequest('/buildings', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

export async function deleteBuilding(buildingId: string) {
  return apiRequest(`/buildings/${buildingId}`, {
    method: 'DELETE',
  })
}

// ============================================
// ПОЛЬЗОВАТЕЛИ
// ============================================

export async function loadUsers(opts?: { includeStudents?: boolean }): Promise<User[]> {
  const q = opts?.includeStudents ? '?include_students=1' : ''
  const users = await apiRequest<User[]>(`/users${q}`)
  console.log('loadUsers API response:', users)
  console.log('Users with vedouci_executor role:', users.filter(u => u.role === 'vedouci_executor'))
  return users
}

export async function loadUsersByRole(role: string): Promise<User[]> {
  return apiRequest<User[]>(`/users?role=${encodeURIComponent(role)}`)
}

/** Zaměstnanci školy ze tabulky school_employees (Správa zaměstnanců školy). */
export async function loadSchoolEmployees(): Promise<User[]> {
  return apiRequest<User[]>('/employee-picker')
}

/** Osobní číslo z users / school_employees (volitelně jiného uživatele při podání za něj). */
export async function loadPersonalNumber(userId?: string): Promise<string | null> {
  const query = userId ? `?user_id=${encodeURIComponent(userId)}` : ''
  const res = await apiRequest<{ personal_number: string | null }>(`/personal-number${query}`)
  const pn = res.personal_number
  return pn && String(pn).trim() !== '' ? String(pn).trim() : null
}


export async function loadExecutors(): Promise<User[]> {
  return apiRequest<User[]>('/users?role=executor')
}

export async function loadAdmins(): Promise<User[]> {
  return apiRequest<User[]>('/users?role=admin')
}

export async function loadVacationSupervisors(): Promise<User[]> {
  return apiRequest<User[]>('/users?role=supervisors')
}

export async function addUser(
  email: string,
  name: string,
  role:
    | 'admin'
    | 'executor'
    | 'director'
    | 'financist'
    | 'referent'
    | 'referent_eko'
    | 'vedouci'
    | 'vedouci_executor'
    | 'property_referent'
    | 'spravce_rozvrhu'
    | 'spravce_jidelny'
    | 'personalista'
    | 'zocmos'
    | 'zastupce_reditele',
  adminType?: string
): Promise<{ id: string; success: boolean; updated?: boolean; created?: boolean; message?: string }> {
  return apiRequest('/users', {
    method: 'POST',
    body: JSON.stringify({
      email,
      name,
      role,
      admin_type: adminType || null,
    }),
  })
}

export async function updateUser(
  userId: string,
  name: string,
  role:
    | 'admin'
    | 'executor'
    | 'director'
    | 'financist'
    | 'referent'
    | 'referent_eko'
    | 'vedouci'
    | 'vedouci_executor'
    | 'property_referent'
    | 'spravce_rozvrhu'
    | 'spravce_jidelny'
    | 'personalista'
    | 'zocmos'
    | 'zastupce_reditele',
  adminType?: 'spravce_it' | 'spravce_budov'
): Promise<{ success: boolean; role?: string; user?: User }> {
  const result = await apiRequest<{ success: boolean; role?: string; user?: User }>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({
      name,
      role,
      admin_type: role === 'admin' ? adminType : undefined,
    }),
  })
  console.log('updateUser API response:', result)
  return result
}

export async function deleteUser(userId: string) {
  return apiRequest(`/users/${userId}`, {
    method: 'DELETE',
  })
}

// ============================================
// УВЕДОМЛЕНИЯ
// ============================================

export async function loadNotifications(_userId: string, limit: number = 50): Promise<Notification[]> {
  return apiRequest<Notification[]>(`/notifications?limit=${limit}`)
}

export async function getUnreadCount(_userId: string) {
  const result = await apiRequest<{ count: number }>(`/notifications/unread-count`)
  return result.count || 0
}

export async function markNotificationRead(notificationId: string, _userId: string) {
  return apiRequest(`/notifications/${notificationId}`, {
    method: 'PUT',
  })
}

export async function markAllNotificationsRead(_userId: string) {
  return apiRequest('/notifications/read-all', {
    method: 'PUT',
  })
}

// Подписка на уведомления (polling вместо Realtime)
export function subscribeToNotifications(
  userId: string,
  callback: (notification: any) => void
) {
  let intervalId: number | null = null
  let lastCheck = Date.now()

  const checkNotifications = async () => {
    try {
      const notifications = await loadNotifications(userId, 1)
      if (notifications.length > 0) {
        const latest = notifications[0]
        if (new Date(latest.created_at).getTime() > lastCheck) {
          callback(latest)
          lastCheck = Date.now()
        }
      }
    } catch (error) {
      console.error('Error checking notifications:', error)
    }
  }

  // Проверяем каждые 5 секунд
  intervalId = window.setInterval(checkNotifications, 5000)

  // Возвращаем функцию для отмены подписки
  return () => {
    if (intervalId !== null) {
      clearInterval(intervalId)
    }
  }
}

// ============================================
// ЦЕНТРЫ (STŘEDISKA)
// ============================================

export async function loadCenters(): Promise<Center[]> {
  return apiRequest<Center[]>('/centers')
}

export async function addCenter(name: string) {
  return apiRequest('/centers', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

export async function deleteCenter(centerId: string) {
  return apiRequest(`/centers/${centerId}`, {
    method: 'DELETE',
  })
}

// ============================================
// ЗАЯВКИ НА ПОКУПКИ
// ============================================

export async function loadPurchaseRequests(_userId: string, _userRole: string | null, purchaseRole?: string | null): Promise<PurchaseRequest[]> {
  const url = purchaseRole ? `/purchase-requests?purchaseRole=${purchaseRole}` : '/purchase-requests'
  const requests = await apiRequest<any[]>(url)

  // Преобразуем в формат PurchaseRequest
  return requests.map((req: any) => ({
    id: req.id,
    created_at: req.created_at,
    updated_at: req.updated_at,
    created_by_id: req.created_by_id,
    created_by_name: req.created_by_name,
    created_by_email: req.created_by_email,
    supervisor_id: req.supervisor_id,
    supervisor_name: req.supervisor_name,
    supervisor_email: req.supervisor_email,
    approved_by_leader_id: req.approved_by_leader_id,
    approved_by_leader_name: req.approved_by_leader_name,
    approved_by_leader_at: req.approved_by_leader_at,
    rejected_by_leader_id: req.rejected_by_leader_id,
    rejected_by_leader_name: req.rejected_by_leader_name,
    rejected_by_leader_at: req.rejected_by_leader_at,
    rejected_by_leader_reason: req.rejected_by_leader_reason,
    cancelled_by_id: req.cancelled_by_id,
    cancelled_at: req.cancelled_at,
    cancelled_reason: req.cancelled_reason,
    phone: req.phone,
    center_id: req.center_id,
    center_name: req.center_name,
    subject: req.subject,
    description: req.description,
    reason: req.reason,
    supplier: req.supplier,
    amount: req.amount ? parseFloat(req.amount) : undefined,
    file_url: req.file_url,
    file_name: req.file_name,
    status: req.status,
    notes: req.notes,
    approved_by_economist_id: req.approved_by_economist_id,
    approved_by_economist_name: req.approved_by_economist_name,
    approved_by_economist_at: req.approved_by_economist_at,
    rejected_by_economist_id: req.rejected_by_economist_id,
    rejected_by_economist_name: req.rejected_by_economist_name,
    rejected_by_economist_at: req.rejected_by_economist_at,
    deferred_by_id: req.deferred_by_id,
    deferred_by_name: req.deferred_by_name,
    deferred_at: req.deferred_at,
    approved_by_director_id: req.approved_by_director_id,
    approved_by_director_name: req.approved_by_director_name,
    approved_by_director_at: req.approved_by_director_at,
    rejected_by_director_id: req.rejected_by_director_id,
    rejected_by_director_name: req.rejected_by_director_name,
    rejected_by_director_at: req.rejected_by_director_at,
    accepted_by_referent_id: req.accepted_by_referent_id,
    accepted_by_referent_name: req.accepted_by_referent_name,
    accepted_by_referent_at: req.accepted_by_referent_at,
    ordered_by_referent_id: req.ordered_by_referent_id,
    ordered_by_referent_name: req.ordered_by_referent_name,
    ordered_by_referent_at: req.ordered_by_referent_at,
    ready_by_referent_id: req.ready_by_referent_id,
    ready_by_referent_name: req.ready_by_referent_name,
    ready_by_referent_at: req.ready_by_referent_at,
    rejected_by_referent_id: req.rejected_by_referent_id,
    rejected_by_referent_name: req.rejected_by_referent_name,
    rejected_by_referent_at: req.rejected_by_referent_at,
    rejected_by_referent_reason: req.rejected_by_referent_reason,
    rejected_by_vedouci_id: req.rejected_by_vedouci_id,
    rejected_by_vedouci_name: req.rejected_by_vedouci_name,
    rejected_by_vedouci_at: req.rejected_by_vedouci_at,
    rejected_by_vedouci_reason: req.rejected_by_vedouci_reason,
    completed_at: req.completed_at,
    completion_comment: req.completion_comment,
  })) as PurchaseRequest[]
}

export async function loadPurchaseRequestsForCabinet(): Promise<PurchaseRequest[]> {
  const requests = await apiRequest<any[]>('/purchase-requests?cabinet=1')
  return requests.map((req: any) => ({
    id: req.id,
    created_at: req.created_at,
    updated_at: req.updated_at,
    created_by_id: req.created_by_id,
    created_by_name: req.created_by_name,
    created_by_email: req.created_by_email,
    supervisor_id: req.supervisor_id,
    supervisor_name: req.supervisor_name,
    supervisor_email: req.supervisor_email,
    approved_by_leader_id: req.approved_by_leader_id,
    approved_by_leader_name: req.approved_by_leader_name,
    approved_by_leader_at: req.approved_by_leader_at,
    rejected_by_leader_id: req.rejected_by_leader_id,
    rejected_by_leader_name: req.rejected_by_leader_name,
    rejected_by_leader_at: req.rejected_by_leader_at,
    rejected_by_leader_reason: req.rejected_by_leader_reason,
    cancelled_by_id: req.cancelled_by_id,
    cancelled_at: req.cancelled_at,
    cancelled_reason: req.cancelled_reason,
    phone: req.phone,
    center_id: req.center_id,
    center_name: req.center_name,
    subject: req.subject,
    description: req.description,
    reason: req.reason,
    supplier: req.supplier,
    amount: req.amount ? parseFloat(req.amount) : undefined,
    file_url: req.file_url,
    file_name: req.file_name,
    status: req.status,
    notes: req.notes,
    approved_by_economist_id: req.approved_by_economist_id,
    approved_by_economist_name: req.approved_by_economist_name,
    approved_by_economist_at: req.approved_by_economist_at,
    rejected_by_economist_id: req.rejected_by_economist_id,
    rejected_by_economist_name: req.rejected_by_economist_name,
    rejected_by_economist_at: req.rejected_by_economist_at,
    deferred_by_id: req.deferred_by_id,
    deferred_by_name: req.deferred_by_name,
    deferred_at: req.deferred_at,
    approved_by_director_id: req.approved_by_director_id,
    approved_by_director_name: req.approved_by_director_name,
    approved_by_director_at: req.approved_by_director_at,
    rejected_by_director_id: req.rejected_by_director_id,
    rejected_by_director_name: req.rejected_by_director_name,
    rejected_by_director_at: req.rejected_by_director_at,
    accepted_by_referent_id: req.accepted_by_referent_id,
    accepted_by_referent_name: req.accepted_by_referent_name,
    accepted_by_referent_at: req.accepted_by_referent_at,
    ordered_by_referent_id: req.ordered_by_referent_id,
    ordered_by_referent_name: req.ordered_by_referent_name,
    ordered_by_referent_at: req.ordered_by_referent_at,
    ready_by_referent_id: req.ready_by_referent_id,
    ready_by_referent_name: req.ready_by_referent_name,
    ready_by_referent_at: req.ready_by_referent_at,
    rejected_by_referent_id: req.rejected_by_referent_id,
    rejected_by_referent_name: req.rejected_by_referent_name,
    rejected_by_referent_at: req.rejected_by_referent_at,
    rejected_by_referent_reason: req.rejected_by_referent_reason,
    rejected_by_vedouci_id: req.rejected_by_vedouci_id,
    rejected_by_vedouci_name: req.rejected_by_vedouci_name,
    rejected_by_vedouci_at: req.rejected_by_vedouci_at,
    rejected_by_vedouci_reason: req.rejected_by_vedouci_reason,
    completed_at: req.completed_at,
    completion_comment: req.completion_comment,
  })) as PurchaseRequest[]
}

export async function loadPurchaseRequestHistory(requestId: string): Promise<PurchaseRequestStatusHistory[]> {
  const history = await apiRequest<any[]>(`/purchase-requests/${requestId}/history`)
  return history.map((h: any) => ({
    id: h.id,
    purchase_request_id: h.purchase_request_id,
    changed_by_id: h.changed_by_id,
    changed_by_name: h.changed_by_name,
    old_status: h.old_status,
    new_status: h.new_status,
    notes: h.notes,
    created_at: h.created_at,
  })) as PurchaseRequestStatusHistory[]
}

export async function deletePurchaseRequest(requestId: string) {
  return apiRequest(`/purchase-requests/${requestId}`, {
    method: 'DELETE',
  })
}

export async function createPurchaseRequest(
  centerId: string,
  subject: string,
  description: string,
  reason: string,
  supplier?: string,
  amount?: number,
  fileUrl?: string,
  fileName?: string,
  supervisorId?: string | null
) {
  return apiRequest('/purchase-requests', {
    method: 'POST',
    body: JSON.stringify({
      center_id: centerId,
      subject,
      description,
      reason,
      supplier: supplier || null,
      amount: amount || null,
      file_url: fileUrl || null,
      file_name: fileName || null,
      supervisor_id: supervisorId || null,
    }),
  })
}

export async function approvePurchaseRequestByEconomist(
  requestId: string,
  notes?: string,
  purchaseRole?: string | null
) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/approve-economist${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ notes: notes || null }),
  })
}

export async function rejectPurchaseRequestByEconomist(
  requestId: string,
  notes?: string,
  purchaseRole?: string | null
) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/reject-economist${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ notes: notes || null }),
  })
}

export async function deferPurchaseRequest(
  requestId: string,
  notes?: string,
  purchaseRole?: string | null
) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/defer${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ notes: notes || null }),
  })
}

export async function approvePurchaseRequestByDirector(requestId: string, notes?: string) {
  return apiRequest(`/purchase-requests/${requestId}/approve-director`, {
    method: 'PUT',
    body: JSON.stringify({ notes: notes || null }),
  })
}

export async function rejectPurchaseRequestByDirector(requestId: string, notes?: string) {
  return apiRequest(`/purchase-requests/${requestId}/reject-director`, {
    method: 'PUT',
    body: JSON.stringify({ notes: notes || null }),
  })
}

export async function loadPurchaseRequestStatusHistory(requestId: string) {
  return apiRequest<any[]>(`/purchase-requests/${requestId}/history`)
}

export async function acceptPurchaseRequestByReferent(requestId: string, purchaseRole?: string | null) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/accept-referent${qs}`, {
    method: 'PUT',
  })
}

export async function orderPurchaseRequestByReferent(requestId: string, purchaseRole?: string | null) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/order-referent${qs}`, {
    method: 'PUT',
  })
}

export async function completePurchaseRequestByReferent(
  requestId: string,
  comment?: string,
  purchaseRole?: string | null
) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/complete-referent${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ comment: comment || null }),
  })
}

export async function rejectPurchaseRequestByVedouci(requestId: string, reason: string, notes?: string) {
  // Legacy alias — žadatel může zrušit jen ve stavu pending_leader
  return apiRequest(`/purchase-requests/${requestId}/cancel-requester`, {
    method: 'PUT',
    body: JSON.stringify({ reason, notes }),
  })
}

export async function approvePurchaseRequestByLeader(requestId: string, purchaseRole?: string | null) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/approve-leader${qs}`, {
    method: 'PUT',
    body: JSON.stringify({}),
  })
}

export async function rejectPurchaseRequestByLeader(
  requestId: string,
  reason: string,
  purchaseRole?: string | null
) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/reject-leader${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  })
}

export async function cancelPurchaseRequestByRequester(requestId: string, reason: string) {
  return apiRequest(`/purchase-requests/${requestId}/cancel-requester`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  })
}

export async function cancelPurchaseRequestByLeader(
  requestId: string,
  reason: string,
  purchaseRole?: string | null
) {
  const qs = purchaseRole ? `?purchaseRole=${encodeURIComponent(purchaseRole)}` : ''
  return apiRequest(`/purchase-requests/${requestId}/cancel-leader${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  })
}

export async function loadPurchaseSupervisors(): Promise<User[]> {
  return apiRequest<User[]>('/users?role=purchase_supervisors')
}

export async function rejectPurchaseRequestByReferent(
  requestId: string,
  reason: string,
  purchaseRole?: string | null
) {
  const role = purchaseRole || 'referent'
  return apiRequest(`/purchase-requests/${requestId}/reject-referent?purchaseRole=${encodeURIComponent(role)}`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  })
}

// ============================================
// ЗАГРУЗКА ФАЙЛОВ
// ============================================

export async function uploadFile(file: File): Promise<{ file_url: string; file_name: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${API_BASE_URL}/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`
    try {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json()
        errorMessage = error.error || error.message || errorMessage
      } else {
        // Если ответ не JSON, читаем как текст
        const text = await response.text()
        // Пытаемся найти сообщение об ошибке в тексте
        if (text.includes('Fatal error') || text.includes('fatal')) {
          errorMessage = 'Fatal error on server. Please contact administrator.'
        } else if (text.trim()) {
          errorMessage = text.substring(0, 200) // Берем первые 200 символов
        }
      }
    } catch (e) {
      // Если не удалось прочитать ответ, используем статус
      errorMessage = response.statusText || `HTTP ${response.status}`
    }
    throw new Error(errorMessage)
  }

  const result = await response.json()

  // Проверяем, что ответ содержит нужные поля
  if (!result.file_url && result.url) {
    // Если есть только url, используем его как file_url
    return {
      file_url: result.url,
      file_name: result.file_name || result.fileName || 'uploaded_file',
    }
  }

  if (!result.file_url) {
    throw new Error('Server response missing file_url')
  }

  return {
    file_url: result.file_url,
    file_name: result.file_name || result.fileName || 'uploaded_file',
  }
}

// ============================================
// ПОЛЬЗОВАТЕЛИ
// ============================================

export async function updateUserRole(userId: string, role: string): Promise<{ success: boolean }> {
  return apiRequest(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  })
}

// ============================================
// СИСТЕМА FKSP (Фонд культурных и социальных потребностей)
// ============================================

export async function loadFkspRequests(
  role?: string,
  options?: { limit?: number; offset?: number },
): Promise<FkspRequest[]> {
  const params = new URLSearchParams()
  if (role) params.set('role', role)
  params.set('limit', String(options?.limit ?? 300))
  if (options?.offset != null && options.offset > 0) {
    params.set('offset', String(options.offset))
  }
  const data = await apiRequest<any[]>(`/fksp-requests?${params.toString()}`)

  return data.map((req) => ({
    ...req,
    amount: req.amount ? parseFloat(req.amount) : undefined,
    fksp_contribution: req.fksp_contribution ? parseFloat(req.fksp_contribution) : undefined,
  }))
}

/** Archiv smazaných žádostek FKSP — pouze správce IT. */
export async function loadFkspDeletedRequests(): Promise<FkspRequest[]> {
  const data = await apiRequest<any[]>('/fksp-requests?deleted=1&limit=10000');
  return data.map(req => ({
    ...req,
    amount: req.amount ? parseFloat(req.amount) : undefined,
    fksp_contribution: req.fksp_contribution ? parseFloat(req.fksp_contribution) : undefined,
  }));
}

export async function createFkspRequest(data: Partial<FkspRequest>): Promise<{ id: string }> {
  return await apiRequest<{ id: string }>('/fksp-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteFkspRequest(id: string): Promise<{ success: boolean }> {
  return await apiRequest<{ success: boolean }>(`/fksp-requests/${id}`, {
    method: 'DELETE',
  })
}

export async function updateFkspRequestStatus(
  id: string, 
  step: 'personalista' | 'zocmos' | 'director', 
  action: 'approve' | 'reject', 
  note?: string
): Promise<{ status: string }> {
  return await apiRequest<{ status: string }>(`/fksp-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ step, action, note }),
  });
}

/** ?role=fksp_financist — aby PUT prošel u správce IT na stránce ekonoma (GET role se u PUT neposílá). */
const fkspFinancistPutQuery = '?role=fksp_financist'

export async function markFkspRequestProcessed(id: string, note?: string): Promise<{ success: boolean }> {
  return await apiRequest<{ success: boolean }>(`/fksp-requests/${id}${fkspFinancistPutQuery}`, {
    method: 'PUT',
    body: JSON.stringify({ action: 'mark_processed', note }),
  })
}

export async function unmarkFkspRequestProcessed(id: string): Promise<{ success: boolean }> {
  return await apiRequest<{ success: boolean }>(`/fksp-requests/${id}${fkspFinancistPutQuery}`, {
    method: 'PUT',
    body: JSON.stringify({ action: 'unmark_processed' }),
  })
}

export async function updateFkspFinancistFields(
  id: string,
  fields: { doplaceno?: number | null; datum?: string | null; note?: string | null },
): Promise<{
  success: boolean
  financist_doplaceno?: number | null
  financist_datum?: string | null
  financist_note?: string | null
}> {
  return await apiRequest(`/fksp-requests/${id}${fkspFinancistPutQuery}`, {
    method: 'PUT',
    body: JSON.stringify({ action: 'update_financist_fields', ...fields }),
  })
}

async function downloadFkspBlob(path: `fksp-download-pdf/${string}`): Promise<void> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}/${path}`, {
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMsg = 'Failed to download file';
    try {
      const errData = (await response.json()) as { error?: string; details?: string };
      errorMsg = errData.error || errorMsg;
      if (errData.details && String(errData.details).trim()) {
        errorMsg = `${errorMsg}: ${String(errData.details).trim().slice(0, 800)}`;
      }
    } catch (e) {}
    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  const date = new Date().toISOString().split('T')[0];
  const fromHeader = filenameFromContentDisposition(response.headers.get('Content-Disposition'));
  a.download = fromHeader || `Zadost_FKSP_${date}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/** type ponecháno kvůli volajícím; název souboru bere z Content-Disposition (jako u dovolené). */
export async function downloadFkspPdf(id: string, _type?: string): Promise<void> {
  await downloadFkspBlob(`fksp-download-pdf/${id}`);
}

export async function updateUserType(userId: string, userType: 'guest' | 'student' | 'employee') {
  return apiRequest(`/users/${userId}/type`, {
    method: 'PUT',
    body: JSON.stringify({ user_type: userType }),
  })
}

// Удаление пользователя (для гостей - самоудаление)
export async function deleteCurrentUser() {
  return apiRequest('/users/me', {
    method: 'DELETE',
  })
}

// ============================================
// СИСТЕМА DOVOLENÁ (Заявления на отпуск)
// ============================================

export async function loadDovolenaRequests(): Promise<DovolenaDashboard> {
  return apiRequest<DovolenaDashboard>('/dovolena');
}

export async function createDovolenaRequest(
  data: Partial<DovolenaRequest> & { days?: DovolenaRequestDay[] }
): Promise<{ id: string }> {
  return await apiRequest<{ id: string }>('/dovolena', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDovolenaRequestStatus(
  id: string,
  action: 'approve' | 'reject'
): Promise<{ success: boolean; status?: string; share_token?: string | null }> {
  return apiRequest(`/dovolena/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ action }),
  });
}

export async function setDovolenaPersonalistaProcessed(
  id: string,
  processed: boolean
): Promise<{ success: boolean }> {
  return apiRequest(`/dovolena/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ action: processed ? 'mark_processed' : 'unmark_processed' }),
  });
}

export async function postDovolenaToAttendance(
  id: string,
  overwrite = false,
): Promise<{
  success: boolean
  ok: boolean
  written: number
  skipped: number
  errors: string[]
  absence_code: string
  already_posted: boolean
  employee_id?: number | null
}> {
  return apiRequest(`/dovolena/${id}/post-to-attendance`, {
    method: 'POST',
    body: JSON.stringify({ overwrite }),
  })
}

export type AttendanceLeaveKind = 'dovolena' | 'samostudium' | 'nemoc'

export type AttendanceLeaveBalance = {
  entitled: number
  used: number
  adjust?: number
  remaining: number
  unit?: 'hours' | 'days'
}

export type AttendanceBankaBalance = {
  balance_minutes: number
  balance_label: string
}

export type AttendanceBankaEntry = {
  id: number
  entry_type: string
  period_yyyymm?: string | null
  delta_minutes: number
  delta_label: string
  balance_after: number
  balance_after_label: string
  note?: string | null
  created_at?: string | null
}

export type AttendanceLeaveEmployeeRow = {
  school_employee_id: number
  employee_name?: string | null
  first_name?: string | null
  last_name?: string | null
  title?: string | null
  title_after?: string | null
  personal_number?: string | null
  dovolena: AttendanceLeaveBalance
  samostudium: AttendanceLeaveBalance
  nemoc?: AttendanceLeaveBalance
}

export type AttendanceBankOverviewRow = {
  employee_id: number
  employee_name?: string | null
  first_name?: string | null
  last_name?: string | null
  title?: string | null
  title_after?: string | null
  personal_number?: string | null
  closed?: boolean
  opening_minutes: number
  opening_label: string
  month_delta_minutes: number
  month_delta_label: string
  closing_minutes: number
  closing_label: string
  current_balance_minutes: number
  current_balance_label: string
  posted?: boolean
}

export async function attendanceLoadLeaveBalances(year: number): Promise<{
  year: number
  employees: AttendanceLeaveEmployeeRow[]
}> {
  const q = new URLSearchParams({ year: String(year) })
  return apiRequest(`/attendance/personalista/leave-balances?${q}`, { method: 'GET' })
}

/** Nastaví aktuální zůstatek DOV (h), SAM (dny) a/nebo nemoc (dny). */
export async function attendanceSaveLeaveBalances(input: {
  school_employee_id: number
  year: number
  dovolena?: number
  samostudium?: number
  nemoc?: number
}): Promise<{
  ok: boolean
  school_employee_id: number
  year: number
  balances: Partial<Record<AttendanceLeaveKind, AttendanceLeaveBalance>>
}> {
  return apiRequest('/attendance/personalista/leave-balances', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendancePersonalistaBankOverview(
  year: number,
  month: number,
): Promise<{ year: number; month: number; employees: AttendanceBankOverviewRow[] }> {
  const q = new URLSearchParams({ year: String(year), month: String(month) })
  return apiRequest(`/attendance/personalista/banka?${q}`, { method: 'GET' })
}

export async function attendanceLoadMyPrehled(
  year: number,
  month: number,
): Promise<AttendanceLeaderPrehled> {
  return apiRequest(
    `/attendance/me/prehled?year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`,
    { method: 'GET' },
  )
}

export async function attendanceLoadMyLeaveBalances(year: number): Promise<{
  year: number
  employee_id: number
  balances: Record<AttendanceLeaveKind, AttendanceLeaveBalance>
}> {
  return apiRequest(`/attendance/me/leave-balances?year=${encodeURIComponent(String(year))}`, {
    method: 'GET',
  })
}

export async function deleteDovolenaRequest(id: string): Promise<{ success: boolean }> {
  return apiRequest(`/dovolena/${id}`, {
    method: 'DELETE',
  })
}

export async function loadDovolenaByShareToken(token: string): Promise<DovolenaRequest> {
  return apiRequest<DovolenaRequest>(`/dovolena/share/${encodeURIComponent(token)}`);
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = header.match(/filename\*=(?:UTF-8'')?([^;\n]+)/i);
  if (star) {
    let raw = star[1].trim().replace(/^"(.*)"$/, '$1');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const quoted = header.match(/filename="([^"]*)"/i);
  if (quoted) return quoted[1];
  const plain = header.match(/filename=([^;\n]+)/i);
  if (plain) return plain[1].trim().replace(/^"(.*)"$/, '$1');
  return null;
}

export async function downloadDovolenaPdf(id: string | number): Promise<void> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}/dovolena-download/${id}`, {
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMsg = 'Failed to download PDF';
    try {
      const errData = (await response.json()) as { error?: string; details?: string };
      errorMsg = errData.error || errorMsg;
      if (errData.details && String(errData.details).trim()) {
        errorMsg = `${errorMsg}: ${String(errData.details).trim()}`;
      }
    } catch (e) {}
    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;

  const fromHeader = filenameFromContentDisposition(response.headers.get('Content-Disposition'));
  const date = new Date().toISOString().split('T')[0];
  a.download = fromHeader || `Zadost_o_dovolenou_zadatel_${date}.pdf`;

  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Проверка данных пользователя в БД (для отладки)
 * Показывает, какой ID в токене и какой в базе данных
 */
export async function checkUserInDatabase() {
  return apiRequest('/users/me/check', {
    method: 'GET',
  })
}

// ============================================
// СИНХРОНИЗАЦИЯ ПОЛЬЗОВАТЕЛЕЙ ИЗ AZURE AD
// ============================================

export interface SyncUsersResult {
  success: boolean
  created: number
  updated: number
  skipped: number
  errors: number
  total_azure_users: number
  error_details?: Array<{ email: string; error: string }>
  backup?: {
    success: boolean
    backup_file: string
    backup_path: string
    file_size: number
    file_size_mb: number
    timestamp: string
    deleted_old_backups: number
  }
}

/**
 * Синхронизация пользователей из Azure AD
 * Создает резервную копию БД перед синхронизацией
 */
export async function syncUsersFromAzure(): Promise<SyncUsersResult> {
  // Синхронизация через admin-db.php для IT администраторов
  return apiRequest<SyncUsersResult>('/admin-db.php?action=sync_users', {
    method: 'GET',
  }, true)
}

// ============================================
// SETTINGS
// ============================================

export async function getSettings(): Promise<Record<string, string>> {
  return apiRequest<Record<string, string>>('/settings')
}

export async function getSetting(key: string): Promise<string | null> {
  try {
    // GET запросы к settings не требуют авторизации (публичные настройки)
    const setting = await apiRequest<{ setting_key: string; setting_value: string }>(`/settings?key=${encodeURIComponent(key)}`, {}, false)
    return setting.setting_value || null
  } catch (error: any) {
    // Игнорируем ошибки авторизации для публичных запросов
    if (error.message === 'UNAUTHORIZED_PUBLIC_REQUEST') {
      return null
    }
    // Для других ошибок тоже возвращаем null, но не логируем
    return null
  }
}

export async function setSetting(key: string, value: string, description?: string): Promise<void> {
  await apiRequest('/settings', {
    method: 'PUT',
    body: JSON.stringify({ key, value, description }),
  })
}

export type CabinetBirthdayPerson = {
  id: number
  name: string
  birth_date: string
  age: number
  is_jubilee: boolean
  position?: string | null
  personal_number?: string | null
}

/** Dnešní narozeniny (ředitel / personalista / ekonom / správce budov|IT). */
export async function loadTodayBirthdays(): Promise<{
  date: string
  birthdays: CabinetBirthdayPerson[]
  count: number
}> {
  return apiRequest('/birthdays-today')
}

export async function deleteSetting(key: string): Promise<void> {
  await apiRequest('/settings', {
    method: 'DELETE',
    body: JSON.stringify({ key }),
  })
}

// ============================================
// ATTENDANCE (Docházkový systém)
// ============================================

export type AttendancePingResponse = {
  ok: boolean
  message?: string
}

export async function attendancePing(): Promise<AttendancePingResponse> {
  return apiRequest<AttendancePingResponse>('/attendance/ping', { method: 'GET' })
}

export type AttendanceAccess = {
  canUser: boolean
  canLeader: boolean
  canPersonalista: boolean
  canAdmin: boolean
  isItAdmin: boolean
  isOversight?: boolean
}

export async function attendanceGetAccess(): Promise<AttendanceAccess> {
  return apiRequest<AttendanceAccess>('/attendance/access', { method: 'GET' })
}

export type AttendanceLeader = {
  id: number
  school_employee_id?: number | null
  user_id?: string | null
  active: 0 | 1
  created_by?: string | null
  created_at: string
  updated_at: string
  user_name?: string
  user_email?: string | null
  personal_number?: string | null
  subordinates_count?: number
}

export type AttendanceLeaderSubordinate = {
  id?: number
  leader_id?: number
  employee_id: number
  created_at?: string
  employee_name?: string
  first_name?: string | null
  last_name?: string | null
  title?: string | null
  work_email?: string | null
  personal_number?: string | null
  employee_user_id?: string | null
}

export async function attendanceLoadLeaders(): Promise<AttendanceLeader[]> {
  return apiRequest<AttendanceLeader[]>('/attendance/leaders', { method: 'GET' })
}

export async function attendanceCreateLeader(input: {
  school_employee_id?: number | string
  user_id?: string
  employee_picker_id?: string
}): Promise<AttendanceLeader> {
  return apiRequest<AttendanceLeader>('/attendance/leaders', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeactivateLeader(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/leaders/${id}`, { method: 'DELETE' })
}

export async function attendanceLoadLeaderSubordinates(
  leaderId: number,
): Promise<AttendanceLeaderSubordinate[]> {
  return apiRequest<AttendanceLeaderSubordinate[]>(
    `/attendance/leaders/${leaderId}/subordinates`,
    { method: 'GET' },
  )
}

export type AttendanceLeaderAssignment = {
  employee_id: number
  leader_id: number
  leader_name?: string | null
}

/** Kdo je už podřízený nějakého aktivního vedoucího (1 zaměstnanec = 1 vedoucí). */
export async function attendanceLoadLeaderAssignments(): Promise<AttendanceLeaderAssignment[]> {
  const res = await apiRequest<{ assignments: AttendanceLeaderAssignment[] }>(
    '/attendance/leaders/assignments',
    { method: 'GET' },
  )
  return Array.isArray(res?.assignments) ? res.assignments : []
}

export async function attendanceSetLeaderSubordinates(
  leaderId: number,
  employeeIds: Array<number | string>,
): Promise<{ ok: boolean; employee_ids: number[] }> {
  return apiRequest(`/attendance/leaders/${leaderId}/subordinates`, {
    method: 'PUT',
    body: JSON.stringify({ employee_ids: employeeIds }),
  })
}

export async function attendanceLoadMySubordinates(): Promise<{
  leader_id: number
  subordinates: AttendanceLeaderSubordinate[]
}> {
  return apiRequest('/attendance/my-subordinates', { method: 'GET' })
}

/** Vestavěné + admin katalog (kód události = punch_type). */
export type AttendancePunchType = string

export type AttendancePunch = {
  id: number
  user_id?: string | null
  punch_type: AttendancePunchType
  punch_type_label: string
  punched_at: string
  building_code: string
  source?: 'self' | 'manual'
  source_label?: string
  created_by_user_id?: string | null
  updated_by_user_id?: string | null
  created_at?: string
}

/** Typy událostí — tlačítka na uživatelském panelu docházky. */
export type AttendanceEventType = {
  id: number
  code: string
  name: string
  kind: 'in' | 'out'
  color: string | null
  sort_order: number
  active: 0 | 1
  /** Započítat interval této události do odpracované doby (0 = pauza). */
  counts_as_work?: 0 | 1
  created_at?: string
  updated_at?: string
}

export async function attendanceLoadEventTypes(opts?: {
  activeOnly?: boolean
}): Promise<AttendanceEventType[]> {
  const q = opts?.activeOnly ? '?active=1' : ''
  return apiRequest<AttendanceEventType[]>(`/attendance/event-types${q}`, { method: 'GET' })
}

export async function attendanceCreateEventType(input: {
  code?: string
  name: string
  kind?: 'in' | 'out'
  color?: string | null
  sort_order?: number
  active?: boolean
  counts_as_work?: boolean
}): Promise<AttendanceEventType> {
  return apiRequest<AttendanceEventType>('/attendance/event-types', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceUpdateEventType(
  id: number,
  input: {
    code?: string
    name?: string
    kind?: 'in' | 'out'
    color?: string | null
    sort_order?: number
    active?: boolean
    counts_as_work?: boolean
  },
): Promise<AttendanceEventType> {
  return apiRequest<AttendanceEventType>(`/attendance/event-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeleteEventType(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/event-types/${id}`, { method: 'DELETE' })
}

export async function attendanceCreatePunch(
  type: AttendancePunchType,
): Promise<AttendancePunch> {
  return apiRequest<AttendancePunch>('/attendance/punches', {
    method: 'POST',
    body: JSON.stringify({ type }),
  })
}

export async function attendanceLoadMyPunches(limit = 50): Promise<AttendancePunch[]> {
  const res = await apiRequest<{ punches: AttendancePunch[] }>(
    `/attendance/punches/mine?limit=${encodeURIComponent(String(limit))}`,
    { method: 'GET' },
  )
  return Array.isArray(res?.punches) ? res.punches : []
}

export type AttendanceLeaderDayCell = {
  day: number
  minutes: number
  hours: number
  hours_label: string
  open: boolean
  punch_count: number
}

export type AttendanceLeaderOverviewSub = {
  employee_id: number
  name: string
  personal_number?: string | null
  work_email?: string | null
  has_user_account: boolean
  user_id?: string | null
  punches_count: number
  month_minutes?: number
  month_hours_label?: string
  /** Období uzavřeno (period lock) — pokud API vrací */
  closed?: boolean
  last_punch?: {
    punched_at: string
    punch_type: string
    punch_type_label: string
    source: string
    building_code: string
  } | null
  /** key = day number as string ("1"…"31") */
  days?: Record<string, AttendanceLeaderDayCell>
}

export type AttendanceLeaderOverview = {
  year: number
  month: number
  days_in_month?: number
  count_doctor_as_work?: boolean
  subordinates: AttendanceLeaderOverviewSub[]
}

export type AttendanceIpBuildingRow = {
  ip: string
  building_code: string
}

export type AttendanceSettings = {
  /** @deprecated Zdroj pravdy = counts_as_work u událostí */
  count_doctor_as_work?: boolean
  stravenka_min_minutes: number
  processing?: {
    overtime_mode: string
    night_from: string
    night_to: string
    late_grace_minutes: number
    stravenka_require_plan: boolean
    wage_codes: Record<string, string>
  }
  /** Veřejná IP → kód budovy u self-punchů (podpis místa). */
  ip_building_map?: AttendanceIpBuildingRow[]
}

export async function attendanceGetSettings(): Promise<AttendanceSettings> {
  return apiRequest('/attendance/settings', { method: 'GET' })
}

export async function attendanceSaveSettings(input: {
  /** @deprecated Preferujte counts_as_work u konkrétní události */
  count_doctor_as_work?: boolean
  stravenka_min_minutes?: number
  processing?: Partial<NonNullable<AttendanceSettings['processing']>> & {
    wage_codes?: Record<string, string>
  }
  ip_building_map?: AttendanceIpBuildingRow[]
}): Promise<AttendanceSettings> {
  return apiRequest('/attendance/settings', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export type AttendanceWorkRegime = {
  id: number
  code?: string | null
  name: string
  description?: string | null
  active: 0 | 1 | boolean
  shift_id?: number | null
  work_schedule_id?: number | null
  created_at?: string
  updated_at?: string
}

export type AttendanceEmployeeRegimeAssignment = {
  school_employee_id: number
  shift_id?: number | null
  work_schedule_id?: number | null
  shift_name?: string | null
  schedule_name?: string | null
  regime_id?: number | null
  regime_code?: string | null
  regime_name?: string | null
  regime_active?: 0 | 1 | boolean
  valid_from?: string | null
  valid_to?: string | null
}

export async function attendanceLoadWorkRegimes(activeOnly = false): Promise<AttendanceWorkRegime[]> {
  const q = activeOnly ? '?active=1' : ''
  return apiRequest(`/attendance/work-regimes${q}`, { method: 'GET' })
}

export async function attendanceCreateWorkRegime(input: {
  code?: string
  name: string
  description?: string
  active?: boolean
  shift_id?: number | null
  work_schedule_id?: number | null
}): Promise<AttendanceWorkRegime> {
  return apiRequest('/attendance/work-regimes', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceUpdateWorkRegime(
  id: number,
  input: {
    code?: string | null
    name?: string
    description?: string | null
    active?: boolean
    shift_id?: number | null
    work_schedule_id?: number | null
  },
): Promise<AttendanceWorkRegime> {
  return apiRequest(`/attendance/work-regimes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeactivateWorkRegime(id: number): Promise<void> {
  await apiRequest(`/attendance/work-regimes/${id}`, { method: 'DELETE' })
}

export async function attendanceLoadEmployeeRegimes(): Promise<AttendanceEmployeeRegimeAssignment[]> {
  const res = await apiRequest<{ assignments: AttendanceEmployeeRegimeAssignment[] }>(
    '/attendance/employee-regimes',
    { method: 'GET' },
  )
  return Array.isArray(res?.assignments) ? res.assignments : []
}

export async function attendanceSaveEmployeeRegimes(
  assignments: Array<{
    school_employee_id: number
    shift_id?: number | null
    work_schedule_id?: number | null
  }>,
): Promise<void> {
  await apiRequest('/attendance/employee-regimes', {
    method: 'PUT',
    body: JSON.stringify({ assignments }),
  })
}

export type AttendanceHoliday = {
  day_date: string
  name: string
  kind: 'statni' | 'custom' | string
  active: boolean | number
  updated_at?: string
}

export async function attendanceLoadHolidays(
  year: number,
): Promise<{ year: number; holidays: AttendanceHoliday[] }> {
  return apiRequest(`/attendance/holidays?year=${encodeURIComponent(String(year))}`, {
    method: 'GET',
  })
}

export async function attendanceSeedHolidays(
  year: number,
): Promise<{ ok: boolean; year: number; inserted: number; skipped: number }> {
  return apiRequest('/attendance/holidays/seed', {
    method: 'POST',
    body: JSON.stringify({ year }),
  })
}

export async function attendanceSaveHoliday(input: {
  day_date: string
  name: string
  kind?: string
  active?: boolean
}): Promise<{ ok: boolean; holiday: AttendanceHoliday }> {
  return apiRequest('/attendance/holidays', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeleteHoliday(dayDate: string): Promise<{ ok: boolean }> {
  return apiRequest(`/attendance/holidays/${encodeURIComponent(dayDate)}`, {
    method: 'DELETE',
  })
}

export async function attendanceLeaderOverview(
  year: number,
  month: number,
): Promise<AttendanceLeaderOverview> {
  return apiRequest(
    `/attendance/leader/overview?year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`,
    { method: 'GET' },
  )
}

export type AttendancePrehledShift = {
  id: number
  code?: string | null
  name: string
  time_from?: string | null
  time_to?: string | null
  break_minutes?: number
  break_from?: string | null
  break_to?: string | null
  label?: string
}

export type AttendancePrehledAbsence = {
  id: number
  code?: string | null
  name: string
  label: string
  hours?: number | null
  start_hour?: number | null
}

export type AttendancePrehledDay = {
  day: number
  day_date: string
  weekday: number
  weekday_short: string
  weekend: boolean
  holiday?: boolean
  holiday_name?: string | null
  shift?: AttendancePrehledShift | null
  absence?: AttendancePrehledAbsence | null
  note?: string | null
  minutes: number
  hours_label: string
  norm_minutes: number
  norm_hours_label: string
  open: boolean
  punch_count: number
  /** Auto přestávka odečtená z faktu (0 = nebyl v práci v okně pauzy). */
  break_deducted_minutes?: number
  stravenka?: boolean
}

export type AttendancePrehledSoucet = {
  norma_minutes: number
  norma_label: string
  odpracovano_minutes: number
  odpracovano_label: string
  saldo_minutes: number
  saldo_label: string
  stravenky_count?: number
  stravenky_min_minutes?: number
}

export type AttendanceBankaSummary = {
  opening_minutes: number
  opening_label: string
  month_delta_minutes: number
  month_delta_label: string
  closing_minutes: number
  closing_label: string
  current_balance_minutes: number
  current_balance_label: string
  posted: boolean
}

export type AttendanceLeaderPrehled = {
  employee_id: number
  employee_name: string
  personal_number?: string | null
  has_user_account: boolean
  user_id?: string | null
  year: number
  month: number
  days_in_month: number
  regime_name?: string | null
  default_shift?: AttendancePrehledShift | null
  closed: boolean
  closed_at?: string | null
  closed_by_user_id?: string | null
  count_doctor_as_work?: boolean
  days: AttendancePrehledDay[]
  punches: AttendancePunch[]
  soucet: AttendancePrehledSoucet
  banka?: AttendanceBankaSummary
  catalog: {
    shifts: AttendancePrehledShift[]
    absence_reasons: AttendancePrehledAbsence[]
    event_types?: Array<{
      id: number
      code: string
      name: string
      kind?: string
      color?: string | null
    }>
  }
}

export type AttendanceLeaderEmployeeBrief = {
  employee_id: number
  name: string
  personal_number?: string | null
  has_user_account: boolean
}

export async function attendanceLeaderLoadEmployees(): Promise<AttendanceLeaderEmployeeBrief[]> {
  const res = await apiRequest<{ employees: AttendanceLeaderEmployeeBrief[] }>(
    '/attendance/leader/employees',
    { method: 'GET' },
  )
  return Array.isArray(res?.employees) ? res.employees : []
}

export async function attendanceLeaderLoadPrehled(
  employeeId: number,
  year: number,
  month: number,
): Promise<AttendanceLeaderPrehled> {
  return apiRequest(
    `/attendance/leader/prehled?employee_id=${encodeURIComponent(String(employeeId))}&year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`,
    { method: 'GET' },
  )
}

export async function attendanceLeaderSavePrehledDay(input: {
  employee_id: number
  day_date: string
  shift_id?: number | null
  absence_reason_id?: number | null
  absence_hours?: number | null
  absence_start_hour?: number | null
  note?: string | null
  clear?: boolean
}): Promise<{ ok: boolean; day: Record<string, unknown> }> {
  return apiRequest('/attendance/leader/prehled/day', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceLeaderGeneratePlan(input: {
  employee_id: number
  year: number
  month: number
  overwrite?: boolean
}): Promise<{ ok: boolean; written: number; skipped: number; source: string }> {
  return apiRequest('/attendance/leader/prehled/generate-plan', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceLeaderCopyMonthPlan(input: {
  employee_id: number
  from_year: number
  from_month: number
  to_year: number
  to_month: number
  overwrite?: boolean
}): Promise<{ ok: boolean; copied: number; skipped: number; from: string; to: string }> {
  return apiRequest('/attendance/leader/prehled/copy-month', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceLeaderClosePeriod(input: {
  employee_id: number
  year: number
  month: number
}): Promise<{ ok: boolean; closed: boolean }> {
  return apiRequest('/attendance/leader/period/close', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export type AttendanceHrOverviewRow = {
  employee_id: number
  name: string
  personal_number?: string | null
  has_user_account: boolean
  punches_count: number
  month_minutes: number
  month_hours_label: string
  open_days: number
  closed: boolean
}

export type AttendanceHrOverview = {
  year: number
  month: number
  count_doctor_as_work?: boolean
  employees: AttendanceHrOverviewRow[]
}

export async function attendancePersonalistaLoadEmployees(): Promise<AttendanceLeaderEmployeeBrief[]> {
  const res = await apiRequest<{ employees: AttendanceLeaderEmployeeBrief[] }>(
    '/attendance/personalista/employees',
    { method: 'GET' },
  )
  return Array.isArray(res?.employees) ? res.employees : []
}

export async function attendancePersonalistaOverview(
  year: number,
  month: number,
): Promise<AttendanceHrOverview> {
  return apiRequest(
    `/attendance/personalista/overview?year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`,
    { method: 'GET' },
  )
}

export async function attendancePersonalistaLoadPrehled(
  employeeId: number,
  year: number,
  month: number,
): Promise<AttendanceLeaderPrehled> {
  return apiRequest(
    `/attendance/personalista/prehled?employee_id=${encodeURIComponent(String(employeeId))}&year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`,
    { method: 'GET' },
  )
}

export async function attendancePersonalistaSavePrehledDay(input: {
  employee_id: number
  day_date: string
  shift_id?: number | null
  absence_reason_id?: number | null
  absence_hours?: number | null
  absence_start_hour?: number | null
  note?: string | null
  clear?: boolean
}): Promise<{ ok: boolean; day: Record<string, unknown> }> {
  return apiRequest('/attendance/personalista/prehled/day', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendancePersonalistaGeneratePlan(input: {
  employee_id: number
  year: number
  month: number
  overwrite?: boolean
}): Promise<{
  ok: boolean
  written?: number
  skipped?: number
  source?: string
}> {
  return apiRequest('/attendance/personalista/prehled/generate-plan', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendancePersonalistaCopyMonthPlan(input: {
  employee_id: number
  from_year: number
  from_month: number
  to_year: number
  to_month: number
  overwrite?: boolean
}): Promise<{ ok: boolean; copied: number; skipped: number; from: string; to: string }> {
  return apiRequest('/attendance/personalista/prehled/copy-month', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendancePersonalistaCreatePunch(
  employeeId: number,
  input: { type: AttendancePunchType; punched_at: string },
): Promise<AttendancePunch> {
  return apiRequest('/attendance/personalista/punches', {
    method: 'POST',
    body: JSON.stringify({ employee_id: employeeId, ...input }),
  })
}

export async function attendancePersonalistaPeriodAction(
  action: 'close' | 'open',
  input: { employee_id?: number; all?: boolean; year: number; month: number },
): Promise<{ ok: boolean; count: number; closed: boolean }> {
  return apiRequest(`/attendance/personalista/period/${action}`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendancePersonalistaReports(
  kind: 'weekends' | 'nights' | 'late' | 'bank_year',
  year: number,
  month?: number,
): Promise<{ kind: string; year: number; month?: number; rows: Array<Record<string, unknown>>; grace_minutes?: number }> {
  const q = new URLSearchParams({ kind, year: String(year) })
  if (month != null) q.set('month', String(month))
  return apiRequest(`/attendance/personalista/reports?${q}`, { method: 'GET' })
}

export async function downloadAttendanceAvensioExport(input: {
  year: number
  month: number
  closed_only?: boolean
  file?: 'd' | 'p' | 'both'
}): Promise<void> {
  const token = getAuthToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const q = new URLSearchParams({
    year: String(input.year),
    month: String(input.month),
    file: input.file || 'both',
    closed_only: input.closed_only === false ? '0' : '1',
  })
  const response = await fetch(`${API_BASE_URL}/attendance-avensio-export?${q}`, {
    headers,
    credentials: 'include',
  })
  if (!response.ok) {
    let errorMsg = 'Avensio export selhal'
    try {
      const errData = (await response.json()) as { error?: string }
      errorMsg = errData.error || errorMsg
    } catch {
      /* ignore */
    }
    throw new Error(errorMsg)
  }
  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  const fromHeader = filenameFromContentDisposition(response.headers.get('Content-Disposition'))
  a.download =
    fromHeader ||
    `Avensio_${input.year}${String(input.month).padStart(2, '0')}.zip`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export async function downloadAttendancePrehledPdf(
  employeeId: number,
  year: number,
  month: number,
): Promise<void> {
  const token = getAuthToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const q = new URLSearchParams({
    employee_id: String(employeeId),
    year: String(year),
    month: String(month),
  })
  const response = await fetch(`${API_BASE_URL}/attendance-prehled-pdf?${q}`, {
    headers,
    credentials: 'include',
  })

  if (!response.ok) {
    let errorMsg = 'Failed to download PDF'
    try {
      const errData = (await response.json()) as { error?: string; details?: string }
      errorMsg = errData.error || errorMsg
      if (errData.details && String(errData.details).trim()) {
        errorMsg = `${errorMsg}: ${String(errData.details).trim()}`
      }
    } catch {
      /* ignore */
    }
    throw new Error(errorMsg)
  }

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  const fromHeader = filenameFromContentDisposition(response.headers.get('Content-Disposition'))
  a.download = fromHeader || `Dochazka_${String(month).padStart(2, '0')}-${year}.pdf`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export async function downloadAttendancePayrollCsv(year: number, month: number): Promise<void> {
  const token = getAuthToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const q = new URLSearchParams({ year: String(year), month: String(month) })
  const response = await fetch(`${API_BASE_URL}/attendance-payroll-export?${q}`, {
    headers,
    credentials: 'include',
  })
  if (!response.ok) {
    let errorMsg = 'Export CSV selhal'
    try {
      const errData = (await response.json()) as { error?: string }
      errorMsg = errData.error || errorMsg
    } catch {
      /* ignore */
    }
    throw new Error(errorMsg)
  }
  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  const fromHeader = filenameFromContentDisposition(response.headers.get('Content-Disposition'))
  a.download = fromHeader || `Dochazka_mzdy_${year}-${String(month).padStart(2, '0')}.csv`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export async function attendancePersonalistaBankDetail(employeeId: number): Promise<{
  employee_id: number
  balance_minutes: number
  balance_label: string
  entries: AttendanceBankaEntry[]
}> {
  const q = new URLSearchParams({ employee_id: String(employeeId) })
  return apiRequest(`/attendance/personalista/banka?${q}`, { method: 'GET' })
}

export async function attendancePersonalistaBankAdjust(input: {
  employee_id: number
  delta_minutes?: number
  /** Absolutní cíl (minuty) — server spočítá deltu. */
  balance_minutes?: number
  note?: string
}): Promise<{ ok: boolean; banka: { balance_minutes: number; balance_label: string; delta_minutes: number } }> {
  return apiRequest('/attendance/personalista/banka', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Hromadně / výběrově vynuluje přepracování (+) nebo nedopracování (−). */
export async function attendancePersonalistaBankNormalize(input: {
  normalize: 'overtime' | 'undertime'
  /** Bez seznamu = všichni s odpovídajícím zůstatkem. */
  employee_ids?: number[]
  note?: string
}): Promise<{
  ok: boolean
  normalize: 'overtime' | 'undertime'
  count: number
  employees: Array<{
    employee_id: number
    previous_minutes: number
    previous_label: string
    balance_minutes: number
    balance_label: string
    delta_minutes: number
  }>
}> {
  return apiRequest('/attendance/personalista/banka', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceUpdateAbsenceReason(
  id: number,
  input: {
    name?: string
    code?: string | null
    wage_code?: string | null
    allow_stravenka?: boolean
    active?: boolean
  },
): Promise<AttendanceSimpleLookup> {
  return apiRequest(`/attendance/absence-reasons/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceLeaderLoadSubordinatePunches(
  employeeId: number,
  year: number,
  month: number,
): Promise<{
  employee_id: number
  employee_name?: string | null
  has_user_account: boolean
  punches: AttendancePunch[]
}> {
  return apiRequest(
    `/attendance/leader/subordinates/${employeeId}/punches?year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`,
    { method: 'GET' },
  )
}

export async function attendanceLeaderCreatePunch(
  employeeId: number,
  input: { type: AttendancePunchType; punched_at: string },
): Promise<AttendancePunch> {
  return apiRequest(`/attendance/leader/subordinates/${employeeId}/punches`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceLeaderUpdatePunch(
  punchId: number,
  input: { type?: AttendancePunchType; punched_at?: string },
): Promise<AttendancePunch> {
  return apiRequest(`/attendance/punches/${punchId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceLeaderDeletePunch(punchId: number): Promise<void> {
  await apiRequest(`/attendance/punches/${punchId}`, { method: 'DELETE' })
}

export type AttendanceGroup = {
  id: number
  code: string | null
  name: string
  active: 0 | 1
  created_at: string
  updated_at: string
}

export async function attendanceLoadGroups(): Promise<AttendanceGroup[]> {
  return apiRequest<AttendanceGroup[]>('/attendance/groups', { method: 'GET' })
}

export async function attendanceCreateGroup(input: { code?: string; name: string }): Promise<AttendanceGroup> {
  return apiRequest<AttendanceGroup>('/attendance/groups', { method: 'POST', body: JSON.stringify(input) })
}

export async function attendanceDeleteGroup(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/groups/${id}`, { method: 'DELETE' })
}

export type AttendanceCardHolder = {
  id: string
  user_id: string | null
  personal_number: string | null
  full_name: string
  email: string | null
  title: string | null
  position: string | null
  group_id: number | null
  group_name?: string | null
  active: 0 | 1
  created_at: string
  updated_at: string
}

export async function attendanceLoadCardHolders(q?: string): Promise<AttendanceCardHolder[]> {
  const qp = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
  return apiRequest<AttendanceCardHolder[]>(`/attendance/card-holders${qp}`, { method: 'GET' })
}

export async function attendanceCreateCardHolder(input: {
  full_name: string
  personal_number?: string
  email?: string
  title?: string
  position?: string
  group_id?: number | null
  user_id?: string | null
}): Promise<AttendanceCardHolder> {
  return apiRequest<AttendanceCardHolder>('/attendance/card-holders', { method: 'POST', body: JSON.stringify(input) })
}

export async function attendanceDeleteCardHolder(id: string): Promise<void> {
  return apiRequest<void>(`/attendance/card-holders/${id}`, { method: 'DELETE' })
}

export type AttendanceCard = {
  id: string
  card_holder_id: string
  card_code: string
  active: 0 | 1
  created_at: string
  updated_at: string
}

export async function attendanceLoadCards(cardHolderId: string): Promise<AttendanceCard[]> {
  return apiRequest<AttendanceCard[]>(`/attendance/cards?card_holder_id=${encodeURIComponent(cardHolderId)}`, { method: 'GET' })
}

export async function attendanceCreateCard(input: { card_holder_id: string; card_code: string }): Promise<AttendanceCard> {
  return apiRequest<AttendanceCard>('/attendance/cards', { method: 'POST', body: JSON.stringify(input) })
}

export async function attendanceDeleteCard(id: string): Promise<void> {
  return apiRequest<void>(`/attendance/cards/${id}`, { method: 'DELETE' })
}

export type AttendanceTerminalManufacturer =
  | 'generic'
  | 'zkteco'
  | 'anviz'
  | 'hikvision'
  | 'suprema'
  | 'other'

export type AttendanceTerminalConnectionMode = 'webhook_push' | 'http_pull' | 'sdk_bridge'

export type AttendanceTerminal = {
  id: number
  name: string
  manufacturer: AttendanceTerminalManufacturer | string
  connection_mode: AttendanceTerminalConnectionMode | string
  building_code: string
  host?: string | null
  port?: number | null
  api_base_url?: string | null
  username?: string | null
  has_password?: boolean
  has_api_token?: boolean
  api_token_preview?: string | null
  api_token?: string | null
  config?: Record<string, unknown> | null
  notes?: string | null
  active: 0 | 1 | boolean
  last_seen_at?: string | null
  created_at?: string
  updated_at?: string
  webhook_path?: string
}

export type AttendanceTerminalsResponse = {
  terminals: AttendanceTerminal[]
  manufacturers: string[]
  connection_modes: string[]
  webhook_path: string
}

export async function attendanceLoadTerminals(): Promise<AttendanceTerminalsResponse> {
  return apiRequest<AttendanceTerminalsResponse>('/attendance/terminals', { method: 'GET' })
}

export async function attendanceGetTerminal(
  id: number,
  revealToken = false,
): Promise<AttendanceTerminal> {
  const q = revealToken ? '?reveal_token=1' : ''
  return apiRequest<AttendanceTerminal>(`/attendance/terminals/${id}${q}`, { method: 'GET' })
}

export async function attendanceSaveTerminal(
  input: Partial<AttendanceTerminal> & {
    name: string
    password?: string
    rotate_api_token?: boolean
    api_token?: string
  },
  id?: number,
): Promise<AttendanceTerminal> {
  if (id) {
    return apiRequest<AttendanceTerminal>(`/attendance/terminals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  }
  return apiRequest<AttendanceTerminal>('/attendance/terminals', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeleteTerminal(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/terminals/${id}`, { method: 'DELETE' })
}

export type AttendanceCredentialType = 'card' | 'chip' | 'fingerprint'

export type AttendanceEmployeeCredential = {
  id: number
  school_employee_id: number
  credential_type: AttendanceCredentialType | string
  credential_value: string
  terminal_id?: number | null
  terminal_name?: string | null
  external_user_id?: string | null
  note?: string | null
  active: 0 | 1 | boolean
  employee_name?: string | null
  first_name?: string | null
  last_name?: string | null
  personal_number?: string | null
  created_at?: string
  updated_at?: string
}

export type AttendanceCredentialsResponse = {
  credentials: AttendanceEmployeeCredential[]
  credential_types: string[]
  terminals: AttendanceTerminal[]
}

export async function attendanceLoadCredentials(params?: {
  school_employee_id?: number
  q?: string
}): Promise<AttendanceCredentialsResponse> {
  const sp = new URLSearchParams()
  if (params?.school_employee_id) sp.set('school_employee_id', String(params.school_employee_id))
  if (params?.q?.trim()) sp.set('q', params.q.trim())
  const q = sp.toString() ? `?${sp.toString()}` : ''
  return apiRequest<AttendanceCredentialsResponse>(`/attendance/credentials${q}`, { method: 'GET' })
}

export async function attendanceSaveCredential(
  input: {
    school_employee_id: number
    credential_type: AttendanceCredentialType | string
    credential_value: string
    terminal_id?: number | null
    external_user_id?: string | null
    note?: string | null
    active?: boolean | number
  },
  id?: number,
): Promise<AttendanceEmployeeCredential> {
  if (id) {
    return apiRequest<AttendanceEmployeeCredential>(`/attendance/credentials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  }
  return apiRequest<AttendanceEmployeeCredential>('/attendance/credentials', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeleteCredential(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/credentials/${id}`, { method: 'DELETE' })
}

export type AttendanceSimpleLookup = {
  id: number
  code: string | null
  name: string
  wage_code?: string | null
  allow_stravenka?: 0 | 1 | boolean
  active: 0 | 1
  created_at: string
  updated_at: string
}

export async function attendanceLoadAbsenceReasons(): Promise<AttendanceSimpleLookup[]> {
  return apiRequest<AttendanceSimpleLookup[]>('/attendance/absence-reasons', { method: 'GET' })
}

export async function attendanceCreateAbsenceReason(input: {
  code?: string
  name: string
  wage_code?: string
  allow_stravenka?: boolean
}): Promise<AttendanceSimpleLookup> {
  return apiRequest<AttendanceSimpleLookup>('/attendance/absence-reasons', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeleteAbsenceReason(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/absence-reasons/${id}`, { method: 'DELETE' })
}

export async function attendanceLoadEventReasons(): Promise<AttendanceSimpleLookup[]> {
  return apiRequest<AttendanceSimpleLookup[]>('/attendance/event-reasons', { method: 'GET' })
}

export async function attendanceCreateEventReason(input: { code?: string; name: string }): Promise<AttendanceSimpleLookup> {
  return apiRequest<AttendanceSimpleLookup>('/attendance/event-reasons', { method: 'POST', body: JSON.stringify(input) })
}

export async function attendanceDeleteEventReason(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/event-reasons/${id}`, { method: 'DELETE' })
}

export type AttendanceShift = {
  id: number
  code?: string | null
  name: string
  wage_code?: string | null
  premium_wage_code?: string | null
  time_from: string
  time_to: string
  break_minutes: number
  break_from?: string | null
  break_to?: string | null
  crosses_midnight: 0 | 1
  fixed_boundaries?: 0 | 1 | boolean
  active: 0 | 1
  created_at: string
  updated_at: string
}

export async function attendanceLoadShifts(): Promise<AttendanceShift[]> {
  return apiRequest<AttendanceShift[]>('/attendance/shifts', { method: 'GET' })
}

export async function attendanceCreateShift(input: {
  code?: string
  name: string
  wage_code?: string
  premium_wage_code?: string
  time_from: string
  time_to: string
  break_from?: string | null
  break_to?: string | null
  break_minutes?: number
  crosses_midnight?: boolean
  fixed_boundaries?: boolean
}): Promise<AttendanceShift> {
  return apiRequest<AttendanceShift>('/attendance/shifts', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceUpdateShift(
  id: number,
  input: {
    code?: string | null
    name?: string
    wage_code?: string | null
    premium_wage_code?: string | null
    time_from?: string
    time_to?: string
    break_from?: string | null
    break_to?: string | null
    break_minutes?: number
    crosses_midnight?: boolean
    fixed_boundaries?: boolean
    active?: boolean
  },
): Promise<AttendanceShift> {
  return apiRequest<AttendanceShift>(`/attendance/shifts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeleteShift(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/shifts/${id}`, { method: 'DELETE' })
}

export type AttendanceWorkScheduleWeekPattern = 'all' | 'odd_even'

export type AttendanceWorkSchedule = {
  id: number
  name: string
  week_pattern?: AttendanceWorkScheduleWeekPattern | string | null
  mon_shift_id: number | null
  tue_shift_id: number | null
  wed_shift_id: number | null
  thu_shift_id: number | null
  fri_shift_id: number | null
  sat_shift_id: number | null
  sun_shift_id: number | null
  mon_even_shift_id?: number | null
  tue_even_shift_id?: number | null
  wed_even_shift_id?: number | null
  thu_even_shift_id?: number | null
  fri_even_shift_id?: number | null
  sat_even_shift_id?: number | null
  sun_even_shift_id?: number | null
  /** Šablona nároku DOV (hodiny), volitelné */
  leave_dovolena_hours?: number | string | null
  /** Šablona nároku SAM (dny), volitelné */
  leave_samostudium_days?: number | string | null
  /** Šablona nároku nemoc (dny), volitelné */
  leave_nemoc_days?: number | string | null
  active: 0 | 1
  created_at: string
  updated_at: string
}

export async function attendanceLoadWorkSchedules(): Promise<AttendanceWorkSchedule[]> {
  return apiRequest<AttendanceWorkSchedule[]>('/attendance/work-schedules', { method: 'GET' })
}

export async function attendanceCreateWorkSchedule(
  input: Partial<AttendanceWorkSchedule> & { name: string },
): Promise<AttendanceWorkSchedule> {
  return apiRequest<AttendanceWorkSchedule>('/attendance/work-schedules', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function attendanceUpdateWorkSchedule(
  id: number,
  input: Partial<AttendanceWorkSchedule>,
): Promise<AttendanceWorkSchedule> {
  return apiRequest<AttendanceWorkSchedule>(`/attendance/work-schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function attendanceDeleteWorkSchedule(id: number): Promise<void> {
  return apiRequest<void>(`/attendance/work-schedules/${id}`, { method: 'DELETE' })
}

export type AttendanceEvent = {
  id: string
  card_holder_id: string
  event_date: string
  time_from: string | null
  time_to: string | null
  event_type: 'presence' | 'absence' | 'note' | 'raw'
  absence_reason_id: number | null
  event_reason_id: number | null
  absence_reason_name?: string | null
  event_reason_name?: string | null
  note: string | null
  source: 'manual' | 'import' | 'system'
  created_by_user_id: string | null
  created_at: string
  updated_at: string
}

export async function attendanceLoadEvents(input: { card_holder_id: string; from: string; to: string }): Promise<AttendanceEvent[]> {
  const qp = `?card_holder_id=${encodeURIComponent(input.card_holder_id)}&from=${encodeURIComponent(input.from)}&to=${encodeURIComponent(input.to)}`
  return apiRequest<AttendanceEvent[]>(`/attendance/events${qp}`, { method: 'GET' })
}

export async function attendanceCreateEvent(input: {
  card_holder_id: string
  event_date: string
  time_from?: string | null
  time_to?: string | null
  event_type?: AttendanceEvent['event_type']
  absence_reason_id?: number | null
  event_reason_id?: number | null
  note?: string | null
}): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>('/attendance/events', { method: 'POST', body: JSON.stringify(input) })
}

export async function attendanceDeleteEvent(id: string): Promise<void> {
  return apiRequest<void>(`/attendance/events/${id}`, { method: 'DELETE' })
}

export async function importUsersFromCSV(file: File): Promise<any> {
  const formData = new FormData()
  formData.append('csv_file', file)

  const response = await fetch(`${API_BASE_URL}/admin-db.php?action=import_users_csv`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to import users')
  }

  return response.json()
}

// ============================================
// SUPPLIERS API
// ============================================

export interface Supplier {
  id: string
  name: string
  ico: string | null
  created_at: string
}

export async function loadSuppliers(): Promise<Supplier[]> {
  return apiRequest<Supplier[]>('/suppliers', {
    method: 'GET',
  })
}

export async function createSupplier(name: string, ico?: string): Promise<Supplier> {
  return apiRequest<Supplier>('/suppliers', {
    method: 'POST',
    body: JSON.stringify({ name, ico: ico || null }),
  })
}

export async function deleteSupplier(id: string): Promise<void> {
  return apiRequest<void>(`/suppliers/${id}`, {
    method: 'DELETE',
  })
}

// ============================================
// ЗАМЕТКИ (NOTES) - для správce budov
// ============================================

export async function loadNotes(search?: string, orderBy: string = 'created_at', orderDir: 'ASC' | 'DESC' = 'DESC'): Promise<Note[]> {
  const params = new URLSearchParams()
  if (search) params.append('search', search)
  params.append('orderBy', orderBy)
  params.append('orderDir', orderDir)

  const url = `/notes?${params.toString()}`
  return apiRequest<Note[]>(url)
}

export async function loadNote(noteId: string): Promise<Note> {
  return apiRequest<Note>(`/notes/${noteId}`)
}

export async function createNote(data: {
  title: string
  content: string
  color_tag?: NoteColorTag
  is_pinned?: boolean
}): Promise<Note> {
  return apiRequest<Note>('/notes', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateNote(noteId: string, data: {
  title: string
  content: string
  color_tag?: NoteColorTag
  is_pinned?: boolean
}): Promise<Note> {
  return apiRequest<Note>(`/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteNote(noteId: string): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>(`/notes/${noteId}`, {
    method: 'DELETE',
  })
}

// ============================================
// CESTOVNÍ PŘÍKAZ (TRAVEL ORDERS)
// ============================================

export interface TravelOrder {
  id: string
  order_number: string
  employee_id: string
  employee_name: string
  employee_title?: string
  employee_position?: string
  employee_address?: string
  employee_birth_number?: string
  employee_phone?: string
  employee_personal_number?: string
  employee_department?: string
  supervisor_id: string
  supervisor_name: string
  purpose: string
  destination_place: string
  destination_country: string
  departure_date: string
  departure_time?: string
  expected_return_date?: string
  expected_return_time?: string
  transport_type?: string
  advance_payment?: number
  advance_payment_date?: string
  estimated_daily_allowance?: number
  estimated_accommodation?: number
  accommodation_required?: number | boolean | string | null
  accommodation_description?: string
  travel_companions?: string
  companions?: TravelOrderCompanion[]
  estimated_transport_cost?: number
  estimated_other_costs?: number
  estimated_total?: number
  account_code?: string
  workplace_code?: string
  service_classification?: string
  actual_return_date?: string
  actual_return_time?: string
  actual_daily_allowance?: number
  actual_accommodation?: number
  actual_transport_cost?: number
  actual_other_costs?: number
  actual_fuel_costs?: number
  vehicle_fuel_type?: string
  vehicle_fuel_liters?: number
  vehicle_fuel_price_per_liter?: number
  vehicle_fuel_receipt_documented?: number | boolean | string | null
  vehicle_insurance_policy_number?: string
  vehicle_consumption_l_per_100km?: number
  vehicle_depreciation_per_km?: number
  vehicle_amortization_total?: number
  vehicle_calculated_phm_liters?: number
  actual_total?: number
  /** PDO/JSON často vrací 0/1 nebo řetězec */
  vehicle_used?: boolean | number | string | null
  vehicle_registration?: string
  vehicle_type?: string
  vehicle_km_start?: number
  vehicle_km_end?: number
  vehicle_km_total?: number
  vehicle_cost_per_km?: number
  vehicle_total_cost?: number
  report_text?: string
  status:
    | 'draft'
    | 'pending_approval'
    | 'approved'
    | 'rejected'
    | 'in_progress'
    | 'pending_return_confirmation'
    | 'pending_completion'
    | 'pending_settlement_supervisor'
    | 'completed'
    | 'closed'
  approved_by?: string
  approved_at?: string
  rejection_reason?: string
  /** Vedoucí vrátil vyúčtování k opravě (stav pending_completion). */
  settlement_return_note?: string | null
  return_reported_at?: string
  return_confirmed_at?: string
  checked_by_financist?: string
  checked_at?: string
  financist_notes?: string
  /** Ekonom označil žádost jako zpracovanou (přehled ekonoma). */
  financist_processed_at?: string | null
  financist_processed_by?: string | null
  /** Po úspěšném odeslání předpisu výplat na personální oddělení. */
  payroll_sent_at?: string | null
  advance_receipt_number?: string
  doc_b_transport_text?: string
  doc_b_authorized_signature_note?: string
  doc_b_report_submitted_date?: string
  doc_b_consent_note?: string
  created_at: string
  updated_at: string
  completed_at?: string
  /** Přílohy k vyúčtování (účtenky, ubytování…) — z tabulky travel_order_settlement_attachments */
  settlement_attachments?: TravelOrderSettlementAttachment[]
  daily_records?: TravelOrderDailyRecord[]
}

export interface TravelOrderSettlementAttachment {
  id: string
  travel_order_id?: string
  /** Den řádku „Průběh cesty“ (YYYY-MM-DD); starší záznamy mohou mít null */
  record_date?: string | null
  file_url: string
  original_filename: string
  description?: string | null
  sort_order?: number
  uploaded_at?: string
}

export interface TravelOrderCompanion {
  id: string
  sort_order: number
  full_name: string
}

export interface TravelOrderDailyRecord {
  id: string
  travel_order_id: string
  record_date: string
  departure_time?: string
  arrival_time?: string
  arrival_date?: string
  transport_means?: string
  distance_km?: number
  meeting_place?: string
  hours_time_lost?: number
  work_performance_from?: string
  work_performance_to?: string
  fare_and_local_transport?: number
  stravne_kc?: number
  overnight_allowance?: number
  incidental_expenses?: number
  leg2_departure_date?: string
  leg2_departure_time?: string
  leg2_arrival_date?: string
  leg2_arrival_time?: string
  leg2_fare?: number
  row_total?: number
  day_route?: string
  hours_worked?: number
  minutes_worked?: number
  breakfast_allowance?: number
  lunch_allowance?: number
  dinner_allowance?: number
  daily_total?: number
  reduction_percentage: number
  reduction_amount: number
  notes?: string
}

export interface SupervisorOption {
  user_id: string
  full_name: string
  role: string
}

/** Globální sazby cestovních příkazů (správa: hlavní ekonom / správce IT). */
export type TravelFinancialSettings = {
  mileage_compensation_kc_per_km: number
  fuel_benzin_95_kc_per_l: number
  fuel_benzin_98_kc_per_l: number
  fuel_nafta_kc_per_l: number
  fuel_elektrina_kc_per_kwh: number
  /** Tuzemské stravné (Kč) dle délky pracovní cesty. */
  tuzemske_stravne_kc_upto_5h: number
  tuzemske_stravne_kc_5_to_12h: number
  tuzemske_stravne_kc_12_to_18h: number
  tuzemske_stravne_kc_over_18h: number
  /** Zahraniční stravné (Kč) dle délky pracovní cesty. */
  zahranicni_stravne_eur_upto_5h: number
  zahranicni_stravne_eur_5_to_12h: number
  zahranicni_stravne_eur_12_to_18h: number
  zahranicni_stravne_eur_over_18h: number
  /** Kurz pro přepočet zahraničního stravného (EUR → Kč). */
  eur_to_czk: number
}

export async function loadTravelFinancialSettings(): Promise<TravelFinancialSettings> {
  return apiRequest<TravelFinancialSettings>('/travel-financial-settings')
}

export async function saveTravelFinancialSettings(
  patch: Partial<TravelFinancialSettings>
): Promise<{ success: boolean; settings: TravelFinancialSettings }> {
  return apiRequest('/travel-financial-settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

export async function loadTravelOrders(
  view: 'employee' | 'supervisor' | 'financist'
): Promise<TravelOrder[]> {
  const response = await apiRequest<{ orders: TravelOrder[] }>(
    `/travel-orders/list?view=${encodeURIComponent(view)}`
  )
  return response.orders
}

export async function loadTravelOrderDetails(orderId: string): Promise<TravelOrder> {
  return apiRequest<TravelOrder>(`/travel-orders/details/${orderId}`)
}

/** Vedoucí pro cestovní příkaz — stejný princip jako dovolená (nastavení IT: travel_supervisor_roles). */
export async function loadTravelOrderSupervisors(): Promise<SupervisorOption[]> {
  const users = await apiRequest<Array<{ id: string; name: string; role: string }>>('/users?role=travel_supervisors')
  return users.map((u) => ({ user_id: u.id, full_name: u.name, role: u.role }))
}

export async function createTravelOrder(data: {
  employee_personal_number: string
  /** Vyplňuje server z přihlášeného účtu (users / staff). */
  employee_name?: string
  employee_title?: string
  employee_position?: string
  employee_address: string
  employee_phone: string
  employee_birth_number?: string
  employee_department?: string
  supervisor_id: string
  purpose: string
  destination_place: string
  destination_country?: string
  departure_date: string
  departure_time: string
  expected_return_date: string
  expected_return_time?: string
  transport_type: string
  vehicle_used?: boolean
  vehicle_type?: string
  vehicle_registration?: string
  advance_payment?: number
  estimated_daily_allowance?: number
  estimated_accommodation?: number
  accommodation_required?: boolean
  accommodation_description?: string
  /** Spolucestující — každý řádek zvlášť (položka 3 ve vzoru). */
  companions?: string[]
  travel_companions?: string
  estimated_transport_cost?: number
  estimated_other_costs?: number
  estimated_total?: number
  advance_payment_date?: string
}): Promise<{ success: boolean; order_id: string; order_number: string }> {
  return apiRequest('/travel-orders/create', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function approveTravelOrder(
  orderId: string,
  data?: {
    estimated_total?: number | null
    advance_payment?: number | null
    advance_payment_date?: string | null
  },
): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/approve/${orderId}`, {
    method: 'POST',
    body: JSON.stringify(data ?? {}),
  })
}

export async function reportTravelOrderReturn(orderId: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/report-return/${orderId}`, {
    method: 'POST',
  })
}

export async function confirmTravelOrderReturn(orderId: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/confirm-return/${orderId}`, {
    method: 'POST',
  })
}

export async function rejectTravelOrder(orderId: string, reason: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/reject/${orderId}`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

/** Řádek denní tabulky ve vyúčtování (odeslání bez id / travel_order_id). */
export type TravelOrderDailyRecordInput = Omit<
  TravelOrderDailyRecord,
  'id' | 'travel_order_id' | 'reduction_percentage' | 'reduction_amount'
> & {
  reduction_percentage?: number
  reduction_amount?: number
  /** alias pro stravne_kc */
  stravne?: number
}

export async function approveTravelOrderSettlement(orderId: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/approve-settlement/${orderId}`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function rejectTravelOrderSettlement(orderId: string, note: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/reject-settlement/${orderId}`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })
}

export async function completeTravelOrder(orderId: string, data: {
  actual_return_date?: string
  actual_return_time?: string
  actual_daily_allowance?: number
  actual_accommodation?: number
  actual_transport_cost?: number
  actual_other_costs?: number
  actual_fuel_costs?: number
  actual_total?: number
  vehicle_used?: boolean
  vehicle_registration?: string
  vehicle_type?: string
  vehicle_km_start?: number
  vehicle_km_end?: number
  vehicle_km_total?: number
  vehicle_cost_per_km?: number
  vehicle_total_cost?: number
  vehicle_insurance_policy_number?: string
  vehicle_consumption_l_per_100km?: number
  vehicle_fuel_type?: string
  vehicle_fuel_type_custom?: string
  vehicle_fuel_liters?: number
  vehicle_fuel_price_per_liter?: number
  vehicle_fuel_receipt_documented?: number | string
  vehicle_depreciation_per_km?: number
  /** Hodnota <select> druhu PHM (pro serverový výběr sazby z nastavení ekonomie). */
  vehicle_fuel_select?: string
  report_text?: string
  daily_records?: TravelOrderDailyRecordInput[]
  /** Nahrané soubory přes /upload — server uloží jen URL z /api/uploads/… */
  settlement_attachments?: Array<{
    file_url: string
    original_filename?: string
    description?: string
    /** Musí odpovídat `record_date` řádku průběhu cesty */
    record_date: string
  }>
  /** true = uložit koncept (stav zůstane „Čeká na vyúčtování“, bez e-mailu vedoucímu). */
  settlement_save_only?: boolean
}): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/complete/${orderId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function closeTravelOrder(orderId: string, notes?: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/close/${orderId}`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  })
}

export async function markTravelOrderProcessed(orderId: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/mark-processed/${orderId}`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function unmarkTravelOrderProcessed(orderId: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/unmark-processed/${orderId}`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function loadTravelPayrollMailSettings(): Promise<{ mail_recipients: string }> {
  return apiRequest<{ mail_recipients: string }>('/travel-orders/payroll-mail-settings')
}

export async function saveTravelPayrollMailSettings(mail_recipients: string): Promise<{ success: boolean }> {
  return apiRequest('/travel-orders/payroll-mail-settings', {
    method: 'POST',
    body: JSON.stringify({ mail_recipients }),
  })
}

export async function sendTravelPayrollToPersonal(params: {
  order_ids: string[]
  period_title: string
  place?: string
  footer_date?: string
  processor_name?: string
}): Promise<{ success: boolean; emailed_to: string[]; grand_total: number; rows_count: number }> {
  return apiRequest('/travel-orders/payroll-send', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/** Náhled předpisu výplat — stažení Excelu bez odeslání e-mailem. */
export async function downloadTravelPayrollXlsx(params: {
  order_ids: string[]
  period_title: string
  place?: string
  footer_date?: string
  processor_name?: string
}): Promise<void> {
  const token = getAuthToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(`${API_BASE_URL}/travel-orders/payroll-download`, {
    method: 'POST',
    headers,
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    let errorMsg = 'Stažení předpisu výplat se nezdařilo'
    try {
      const errData = (await response.json()) as { error?: string; details?: string }
      errorMsg = errData.error || errorMsg
      if (errData.details && String(errData.details).trim()) {
        errorMsg = `${errorMsg}: ${String(errData.details).trim().slice(0, 800)}`
      }
    } catch {
      /* ignore */
    }
    throw new Error(errorMsg)
  }

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  const fromHeader = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1]
  const safePeriod = params.period_title.trim().replace(/[^a-zA-Z0-9._-]+/g, '_') || 'obdobi'
  a.download = fromHeader || `Predpis_vyplat_CP_${safePeriod}.xlsx`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export type TravelOrderDocumentBEditInput = {
  employee_name?: string
  employee_title?: string
  employee_address?: string
  employee_personal_number?: string
  employee_department?: string
  employee_phone?: string
  departure_date?: string
  departure_time?: string
  destination_place?: string
  purpose?: string
  actual_return_date?: string
  actual_return_time?: string
  travel_companions?: string
  transport_type?: string
  estimated_total?: number | string
  advance_payment?: number | string
  advance_payment_date?: string
  advance_receipt_number?: string
  doc_b_transport_text?: string
  doc_b_authorized_signature_note?: string
  doc_b_report_submitted_date?: string
  doc_b_consent_note?: string
}

export async function updateTravelOrderDocumentB(
  orderId: string,
  data: TravelOrderDocumentBEditInput
): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/update-document-b/${orderId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function deleteTravelOrder(orderId: string): Promise<{ success: boolean }> {
  return apiRequest(`/travel-orders/${orderId}`, {
    method: 'DELETE',
  })
}

/** Stažení vyplněné šablony cestovního příkazu (Excel, list A/B). */
export async function downloadTravelOrderExcel(
  orderId: string,
  orderNumber: string,
  part: 'a' | 'b' = 'a'
): Promise<void> {
  const token = getAuthToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(`${API_BASE_URL}/travel-order-xlsx/${orderId}?part=${part}`, {
    headers,
    credentials: 'include',
    cache: 'no-store',
  })

  if (!response.ok) {
    let errorMsg = 'Stažení Excelu se nezdařilo'
    try {
      const errData = (await response.json()) as { error?: string; details?: string }
      errorMsg = errData.error || errorMsg
      if (errData.details && String(errData.details).trim()) {
        errorMsg = `${errorMsg}: ${String(errData.details).trim().slice(0, 800)}`
      }
    } catch (e) {
      /* ignore */
    }
    throw new Error(errorMsg)
  }

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  const safe = orderNumber.replace(/[^a-zA-Z0-9_-]+/g, '_')
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  const cd = response.headers.get('Content-Disposition') || ''
  const named = /filename="([^"]+)"/i.exec(cd)
  a.download = named?.[1] || `Cestovni_prikaz_${part.toUpperCase()}_${safe}_${stamp}.xlsx`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export type ItAssetItem = {
  id: number
  category: string
  inventory_number: string
  name: string
  su_au?: string | null
  accounting_price?: number | null
  location?: string | null
  year_acquired?: number | null
  note?: string | null
  monitor_location?: string | null
  parent_inventory?: string | null
  source_file?: string | null
  as_of_date?: string | null
}

export type ItAssetsResponse = {
  as_of?: string
  counts?: { total?: number; by_category?: Record<string, number> }
  items: ItAssetItem[]
}

/** Evidence IT majetku (ACL: správce IT vždy + seznam z nastavení). */
export async function getItAssets(params?: {
  category?: string
  q?: string
}): Promise<ItAssetsResponse> {
  const qs = new URLSearchParams()
  if (params?.category && params.category !== 'all') qs.set('category', params.category)
  if (params?.q?.trim()) qs.set('q', params.q.trim())
  const query = qs.toString()
  return apiRequest<ItAssetsResponse>(`/it-assets${query ? `?${query}` : ''}`)
}

export async function getItAssetsAccess(): Promise<{ canAccess: boolean }> {
  return apiRequest<{ canAccess: boolean }>('/it-assets/access', { method: 'GET' })
}

export async function getItAssetsViewers(): Promise<{
  configured: boolean
  user_ids: string[]
  setting_key?: string
}> {
  return apiRequest('/it-assets/viewers', { method: 'GET' })
}

export async function setItAssetsViewers(userIds: string[]): Promise<{
  ok: boolean
  configured: boolean
  user_ids: string[]
}> {
  return apiRequest('/it-assets/viewers', {
    method: 'PUT',
    body: JSON.stringify({ user_ids: userIds }),
  })
}

// ===============================
// Vehicle fleet / rezervace vozidel
// ===============================

export type VehicleFleetAccess = {
  canAdmin: boolean
  canFleetAdmin: boolean
  canDriver: boolean
  isItAdmin: boolean
  isFleetAdmin: boolean
  isDirector?: boolean
  isDriver: boolean
  /** Schválení žádostí o rezervaci (ředitel / IT). */
  canApproveReservations?: boolean
}

export type VehicleFleetMember = {
  id: number
  user_id: string
  school_employee_id?: number | null
  active: 0 | 1 | boolean
  display_name?: string | null
  email?: string | null
  user_name?: string | null
  created_at?: string
  updated_at?: string
}

export async function vehicleFleetGetAccess(): Promise<VehicleFleetAccess> {
  return apiRequest<VehicleFleetAccess>('/vehicle-fleet/access', { method: 'GET' })
}

export async function vehicleFleetLoadAdmins(): Promise<VehicleFleetMember[]> {
  return apiRequest<VehicleFleetMember[]>('/vehicle-fleet/admins', { method: 'GET' })
}

export async function vehicleFleetAppointAdmin(userId: string): Promise<VehicleFleetMember> {
  return apiRequest<VehicleFleetMember>('/vehicle-fleet/admins', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  })
}

export async function vehicleFleetDeactivateAdmin(id: number): Promise<void> {
  await apiRequest(`/vehicle-fleet/admins/${id}`, { method: 'DELETE' })
}

export async function vehicleFleetLoadDrivers(): Promise<VehicleFleetMember[]> {
  return apiRequest<VehicleFleetMember[]>('/vehicle-fleet/drivers', { method: 'GET' })
}

export async function vehicleFleetAppointDriver(userId: string): Promise<VehicleFleetMember> {
  return apiRequest<VehicleFleetMember>('/vehicle-fleet/drivers', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  })
}

export async function vehicleFleetDeactivateDriver(id: number): Promise<void> {
  await apiRequest(`/vehicle-fleet/drivers/${id}`, { method: 'DELETE' })
}

export type VehicleFleetVehicle = {
  id: number
  name: string
  model: string
  plate_number: string
  active: 0 | 1 | boolean
  /** Stálý řidič vozidla (user id). */
  assigned_driver_user_id?: string | null
  assigned_driver_name?: string | null
  assigned_driver_email?: string | null
  created_at?: string
  updated_at?: string
}

export type VehicleFleetReservation = {
  id: number
  vehicle_id: number
  user_id: string
  starts_at: string
  ends_at: string
  status: 'pending' | 'active' | 'cancelled' | 'rejected' | string
  note?: string | null
  vehicle_name?: string
  vehicle_model?: string
  plate_number?: string
  driver_name?: string | null
  driver_email?: string | null
  assigned_driver_user_id?: string | null
  /** Rezervace stálého řidiče vozidla — blokuje ostatní na termín. */
  is_assigned_driver_reservation?: boolean | 0 | 1
  /** Žádost čeká na schválení (řidič není stálý u vozidla). */
  needs_approval?: boolean | 0 | 1
  reviewed_by?: string | null
  reviewed_at?: string | null
  cancelled_at?: string | null
  created_at?: string
}

export async function vehicleFleetLoadVehicles(activeOnly?: boolean): Promise<VehicleFleetVehicle[]> {
  const q = activeOnly ? '?active=1' : ''
  return apiRequest<VehicleFleetVehicle[]>(`/vehicle-fleet/vehicles${q}`, { method: 'GET' })
}

export async function vehicleFleetCreateVehicle(input: {
  name: string
  model: string
  plate_number: string
  assigned_driver_user_id?: string | null
}): Promise<VehicleFleetVehicle> {
  return apiRequest<VehicleFleetVehicle>('/vehicle-fleet/vehicles', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function vehicleFleetUpdateVehicle(
  id: number,
  input: Partial<{
    name: string
    model: string
    plate_number: string
    active: boolean
    assigned_driver_user_id: string | null
  }>,
): Promise<VehicleFleetVehicle> {
  return apiRequest<VehicleFleetVehicle>(`/vehicle-fleet/vehicles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export async function vehicleFleetDeactivateVehicle(id: number): Promise<void> {
  await apiRequest(`/vehicle-fleet/vehicles/${id}`, { method: 'DELETE' })
}

/** Trvalé smazání (blokováno při aktivních rezervacích). */
export async function vehicleFleetDeleteVehicle(id: number): Promise<void> {
  await apiRequest(`/vehicle-fleet/vehicles/${id}?hard=1`, { method: 'DELETE' })
}

export async function vehicleFleetLoadFreeVehicles(from: string, to: string): Promise<{
  from: string
  to: string
  vehicles: VehicleFleetVehicle[]
}> {
  const q = new URLSearchParams({ from, to })
  return apiRequest(`/vehicle-fleet/free-vehicles?${q}`, { method: 'GET' })
}

export async function vehicleFleetLoadReservations(opts?: {
  all?: boolean
  /** Všechny aktivní rezervace autoparku (pro kalendář / volná vozidla). */
  fleet?: boolean
}): Promise<VehicleFleetReservation[]> {
  const qs = new URLSearchParams()
  if (opts?.all) qs.set('all', '1')
  if (opts?.fleet) qs.set('fleet', '1')
  const q = qs.toString()
  return apiRequest<VehicleFleetReservation[]>(
    `/vehicle-fleet/reservations${q ? `?${q}` : ''}`,
    { method: 'GET' },
  )
}

export async function vehicleFleetCreateReservation(input: {
  vehicle_id: number
  starts_at: string
  ends_at: string
  note?: string
}): Promise<VehicleFleetReservation> {
  return apiRequest<VehicleFleetReservation>('/vehicle-fleet/reservations', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function vehicleFleetCancelReservation(id: number): Promise<VehicleFleetReservation> {
  return apiRequest<VehicleFleetReservation>(`/vehicle-fleet/reservations/${id}`, {
    method: 'DELETE',
  })
}

export async function vehicleFleetApproveReservation(id: number): Promise<VehicleFleetReservation> {
  return apiRequest<VehicleFleetReservation>(`/vehicle-fleet/reservations/${id}/approve`, {
    method: 'POST',
  })
}

export async function vehicleFleetRejectReservation(id: number): Promise<VehicleFleetReservation> {
  return apiRequest<VehicleFleetReservation>(`/vehicle-fleet/reservations/${id}/reject`, {
    method: 'POST',
  })
}
